import { HeroCard } from "./components/ChildHero.jsx";
import { OwnerNameSheet } from "./components/OwnerNameSheet.jsx";
import { actingAdultHere, adultLabel, stampActor, ownerOf} from "./lib/adults.js";
import { coachEnabled } from "./lib/coachEnabled.js";
import { ChildView } from "./components/ChildView.jsx";
import { HomeworkReceive } from "./components/HomeworkReceive.jsx";
import { RequestsOverlay } from "./components/RequestsOverlay.jsx";
import { AppHeaderBrand, AppLegalFooter, CopyrightFooter, Icon, SafeAreaSpacer, characterName } from "./components/Common.jsx";
import { IconGallery } from "./components/IconGallery.jsx";
import { DeviceKidsToggleScreen, DeviceRoleConfirmScreen, DeviceWhoseScreen, HelpModal, InstallButton, KidProfileSwitcher, NewChildVisibilityPrompt, ParentNotificationPrompt, ParentProfileSwitcher, PinPad, resyncPushSubscription } from "./components/Onboarding.jsx";
import { ParentView } from "./components/ParentView.jsx";
import { SetupCompleteOverlay, SetupWizard, WelcomeSetupCard, hasSeenSetupComplete } from "./components/Wizard.jsx";
import { getDeviceId, apiSubmitWordbank, wordbankFlushPlan, WORDBANK_FLUSH_DELAY_MS, apiVerifyPin, ensureProvenDevice, setActiveAdultId as setSessionAdultId, getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode, setDeviceOwnerAdultId, markParentUnlocked, clearParentUnlocked, parentUnlockedRecently, parentUnlockStampedAt, PARENT_GRACE_MS} from "./lib/api.js";
import { ACTIVE_CHILD_KEY_PREFIX, CURRENCY_SYMBOL, DEVICE_ROLE_KEY_PREFIX, HIDDEN_CHILDREN_KEY_PREFIX, MUTED_CHILDREN_KEY_PREFIX, PARENT_TAB_KEY_PREFIX, PENDING_DEVICE_SETUP_KEY_PREFIX, REQUESTS_OVERLAY_SHOWN_KEY_PREFIX, SEEN_CHILDREN_KEY_PREFIX, nowIso, uid, CREATOR_KEY_PREFIX, OWNER_NAME_MAX} from "./lib/constants.js";
import { BADGE_OINK_BONUS, checkAndAwardBadges, findBadge } from "./lib/badges.js";
import { ageBand, onAppError, setMetricsBand, setMetricsMode, setMetricsScreen, track } from "./lib/metrics.js";
import { PERFECT_WEEK_OP, dailyQuestionOP, isDoubleOinkDay, isDoubleTokenDay, recordDailyQuestionForPerfectWeek } from "./lib/dailyQuestion.js";
import { adaptiveParams, adaptiveRecord, gameState as adaptiveGameState } from "./lib/adaptive.js";
import { cmsConfig } from "./lib/content.js";
import { curriculumItemForWord, effectiveSpellingListOrder, effectiveSpellingLists, resolveSpellingList, spellingListItems } from "./lib/spelling.js";
import { recordCustomSession, recordLogOnly } from "./lib/coach-custom.js";
import { recordWordResults } from "./lib/coach-words.js";
import { makeHistoryWriter } from "./lib/testHistory.js";
import { homeworkUnlocked, HW_SHARED_TYPE, HW_SUBMISSION_TYPE, isHomeworkRequest, applyGo, childHasSameList, queueSpellingAudio, archiveList, canGoLive, canSubmitHomework, findHomeworkList, findPendingShared, homeworkLists, mintHomeworkCode, newHomeworkList, pruneHomework, sharedListRequest } from "./lib/homework.js";
import { clearGuestResult, clearHomeworkInbox, fetchHomeworkInbox, fetchSharedList, readGuestResult, sharedCodeFromUrl, takePendingSharedCode } from "./lib/homework-share.js";
import { effectiveVocabCategories, effectiveVocabCategoryOrder, resolveVocabCategory } from "./lib/vocab.js";
import { effectiveMathsBandOrder, effectiveMathsBands, mathsBandTypeIds, resolveMathsBand } from "./lib/maths.js";
import { applyEquip, ensureCosmetics, findCharacterItem, findOinkItem, giftOinks, oinkGiftTakeBack, recordOinkEarn, reverseOinkGift, ownedCharacters } from "./lib/oinks.js";
import { effectiveAge } from "./lib/age.js";
import { capStatusForNewEarning, fmtPence, getKidTheme, goalTargetTokens, habitWeekProgress, isHabitDayComplete, raiseHabitReward, todayStr, weekStartOf } from "./lib/utils.js";
import { openLoan } from "./lib/askToBuy.js";
import { installBackStack, registerTabs, resetRoot, tabChanged, useBackLayer } from "./lib/backstack.js";
// CMS-managed copy resolver (docs/CMS_SCOPE.md §2), imported as `ct` rather than `t` —
// this codebase uses `t` pervasively as a local variable name for transactions, timeout
// ids, and parsed ints, so a bare `t` import here would create real shadowing risk at
// several existing call sites. `ct(key, fallback, vars)` falls back to `fallback`
// (the string that used to live at each call site) whenever the CMS has no published
// override, so this migration changes no visible behaviour until content is published.
import { t as ct } from "./lib/content.js";
const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// LB-343 — who is doing this. Same helper as SettingsTab's, for the same reason.
const actor = (s) => actingAdultHere(s, getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode());

// W2-PR(ii) actor stamping (review 3.6, data half): every transaction this client
// writes records WHICH device wrote it. Resolved once per module load — the id can't
// change mid-session. Display (a "via {device label}" suffix in history) rides
// W2-PR(iii)'s audit UI; this PR makes the record exist from now on.
const DEVICE_ID = getDeviceId();

// Human-readable label for a Learn & Earn game kind, used when a capped reward is turned
// into a pending request's title (see onLearnEarn below) — mirrors the labels shown in the
// kid-facing game picker (see ChildView.jsx's LEARN_EARN_GAMES).
const LEARN_EARN_KIND_LABEL = {
  spelling: "Spelling test",
  vocab: "Vocabulary test",
  ttstreaks: "TT streaks",
  mathsworkout: "Maths workout",
  homework: "Homework practice",
};

export function PullToRefresh({ onRefresh, children }) {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  // Mounted-ness must live OUTSIDE the gesture effect. A flag scoped inside it is torn
  // down by the effect's own cleanup on every re-render — and the touchend handler
  // re-renders by design (setRefreshing/setPullY), with `onRefresh` changing identity
  // each render on top of that. A flag there means "the effect re-ran", never "the
  // component went away", so the spinner's completion callback bailed every single time
  // and the flag spun forever. Found by mounting this component in jsdom and driving a
  // real gesture; the isolated promise-chain test I wrote first could not see it.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    function atTop() {
      return (window.scrollY || document.documentElement.scrollTop || 0) <= 0;
    }
    // A gesture that starts inside a modal is the modal's, not the page's (Alan, 30 Aug
    // 2026: pulling down in the Spending review dragged the background and fired a
    // refresh). These listeners are on `window`, so they see every touch in the document
    // including ones inside a portalled dialog — and `atTop()` measures the PAGE, which is
    // pinned behind an open modal and therefore always at the top. The result was that any
    // downward drag in a modal looked exactly like a pull-to-refresh.
    //
    // `overscroll-contain` on the modal body does not help here: that governs scroll
    // CHAINING, and this is a JS touch handler that never consults scroll at all.
    function fromModal(target) {
      return !!(target && target.closest && target.closest('[role="dialog"], [data-no-pull-refresh]'));
    }
    function onTouchStart(e) {
      if (refreshing) return;
      if (fromModal(e.target)) { startYRef.current = null; return; }
      startYRef.current = atTop() ? e.touches[0].clientY : null;
    }
    function onTouchMove(e) {
      if (startYRef.current === null || refreshing) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0 || !atTop()) { setPullY(0); return; }
      e.preventDefault();
      setPullY(Math.min(delta * 0.5, PTR_MAX_PULL));
    }
    function onTouchEnd() {
      if (startYRef.current === null) return;
      startYRef.current = null;
      if (pullY >= PTR_THRESHOLD) {
        setRefreshing(true);
        setPullY(PTR_THRESHOLD);
        // onRefresh used to be window.location.reload(), so nothing ever needed to clear
        // `refreshing` — the page went away. Now that it is a soft refresh the spinner has
        // to be taken down, or it spins forever AND the `if (refreshing) return` guards
        // above make the gesture dead for the rest of the session.
        //
        // Minimum 500ms even if the refetch resolves instantly: a spinner that vanishes
        // the same frame it appears reads as "nothing happened", which is the one thing a
        // refresh gesture must not do. Errors are swallowed on purpose — Root's refetch is
        // already silent on failure by design, and a failed background pull is not worth a
        // stuck spinner.
        const started = Date.now();
        Promise.resolve()
          .then(() => onRefresh())
          .catch(() => {})
          .then(() => new Promise((r) => setTimeout(r, Math.max(0, 500 - (Date.now() - started)))))
          .then(() => {
            if (!mountedRef.current) return;
            setRefreshing(false);
            setPullY(0);
          });
      } else {
        setPullY(0);
      }
    }
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [pullY, refreshing, onRefresh]);

  const indicatorHeight = refreshing ? PTR_THRESHOLD : pullY;
  const pastThreshold = pullY >= PTR_THRESHOLD;

  return (
    <>
      <div className="flex items-center justify-center overflow-hidden text-slate-400" style={{ height: indicatorHeight, transition: refreshing ? "none" : "height 0.15s ease-out" }}>
        {indicatorHeight > 4 && (
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className={refreshing ? "animate-spin inline-block" : "inline-block"} style={!refreshing ? { transform: `rotate(${pastThreshold ? 180 : 0}deg)`, transition: "transform 0.15s ease" } : undefined}>🔄</span>
            {refreshing ? "Refreshing…" : pastThreshold ? "Release to refresh" : "Pull to refresh"}
          </div>
        )}
      </div>
      {children}
    </>
  );
}


// Parent Mode's bottom tab bar (R-B restructure, Aug 2026) — replaces the old top pill bar
// AND the floating KidModeFab (Kid Mode exit is now the header's "🔓 Kid Mode" button alone;
// see App's own comment on that button and the KidModeFab-removal note in the PR). Mirrors
// ChildView.jsx's own fixed-bottom nav markup/classes exactly (fixed, z-20, safe-area bottom
// padding, max-w-lg inner, plain emoji icons) for visual consistency between the two bottom
// bars — this one just carries a pending-requests badge instead of ChildView's coin icon.
// Gated by the caller exactly as the old pill bar was: mode==="parent" && !wizardActive &&
// parentTab!=="settings". Every tap routes through attemptNav, same as the old pill buttons.
// R-C polish (Aug 2026, punch-list 1): the KidModeFab is BACK — removed in R-B in favour of
// the header button alone, reinstated on Alan's staging review. Lifted above the bottom tab
// bar (offset = safe-area + bar height + margin) so the two never overlap. aria-label is
// deliberately NOT "🔓 Kid Mode": all 30 existing spec matches for the header button use
// { name: "🔓 Kid Mode", exact: true }, and the label here keeps this FAB out of every one
// of them while still reading correctly to screen readers.
// Alan, 27 Sep (Set homework's Name field on his phone): the FAB is fixed 100px above the
// bottom of the LAYOUT viewport, so when the phone's keyboard opens it rides up with the page
// and lands on whatever sits there, which on a form is the step's own CTA ("Next: the
// words"). While a text field has focus on a touch screen the keyboard is up and the FAB
// stands down; it comes back when the field loses focus. focusout is read on the next tick
// because moving from one field to the next blurs before it focuses. Coarse pointers only:
// a laptop has no on-screen keyboard to make room for.
const TYPING_INPUT_TYPES = new Set(["text", "search", "email", "tel", "url", "number", "password", "date", "time", "datetime-local", "month", "week"]);
function isTypingTarget(el) {
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName !== "INPUT") return false;
  return TYPING_INPUT_TYPES.has(String(el.type || "text").toLowerCase());
}
function useSoftKeyboardOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const coarse = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
    if (!coarse) return undefined;
    let timer = null;
    const onIn = (e) => { if (isTypingTarget(e.target)) { clearTimeout(timer); setOpen(true); } };
    const onOut = () => { clearTimeout(timer); timer = setTimeout(() => setOpen(isTypingTarget(document.activeElement)), 0); };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => { clearTimeout(timer); document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
  }, []);
  return open;
}

function KidModeFab({ onClick }) {
  // Portalled for the same containing-block reason as ParentBottomNav above — it is
  // positioned relative to the bar, so the two have to escape together or they drift apart.
  return ReactDOM.createPortal((
    <button
      onClick={onClick}
      aria-label="Switch to Kid Mode"
      // Device pass (Alan, 8 Sep): same tinted chrome as the header button it stands in for, and
      // the right edge tracks the content column (--shell-max is 512px on phones and 800px on
      // wide screens; the old 15rem was the phone column only, so on a laptop it floated inboard).
      className="fixed z-30 bg-pill text-navy rounded-lg px-4 py-3 text-sm font-bold shadow-lg"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 100px)", right: "max(1rem, calc(50vw - (var(--shell-max) / 2) + var(--gutter)))" }}
    ><Icon id="ic-unlock" size={16} className="inline-block align-[-3px] mr-1.5" /><span>{ct("parent.kid_mode", "Kid Mode")}</span></button>
  ), document.body);
}

/* Portalled to document.body (Alan, 30 Aug 2026: "bottom nav bar not pinned to the bottom
 * of the screen").
 *
 * `position: fixed` is measured against the viewport ONLY while no ancestor establishes a
 * containing block — and transform, filter, perspective, contain and will-change all do,
 * including a transform that only exists inside an @keyframes. This app has several
 * (`.pop` on ParentView's root, the pull-to-refresh indicator, celebration animations), so
 * whether the bar is pinned depends on which ancestors happen to be animating at the time,
 * which is exactly why it reads as intermittent. This is the same failure class the
 * KidDetail focus windows and the Home child picker were already portalled out of; the bar
 * is the third instance, so it gets the same treatment rather than a fourth investigation.
 *
 * Portalling to body puts it outside every one of those subtrees, so the viewport is the
 * only thing it can be measured against.
 */
// LB-363 follow-up (Alan, 27 Sep): the Kid Mode button floats bottom-right and covers the photo
// step's "Read the words". PhotoCapture says when that step is open and this stands the button
// down, the same way useSoftKeyboardOpen already does for the keyboard.
function usePhotoStepOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setOpen(true);
    const off = () => setOpen(false);
    window.addEventListener("lb:photo-open", on);
    window.addEventListener("lb:photo-closed", off);
    return () => { window.removeEventListener("lb:photo-open", on); window.removeEventListener("lb:photo-closed", off); };
  }, []);
  return open;
}

function ParentBottomNav({ parentTab, onSelectTab, pendingCount }) {
  return ReactDOM.createPortal((
    <div className="fixed bottom-0 left-0 right-0 z-20 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.06)] lb-frame-bar md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-full md:max-w-[calc(var(--shell-max)-32px)] md:bottom-3 md:rounded-lg" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
      <div className="max-w-lg mx-auto px-3 pt-2 pb-2">
        {/* Pass R: on wide screens the three tabs are fixed-width and centred instead of
            each stretching to a third of a 1000px bar. */}
        <div className="flex gap-1 md:justify-center">
          {PARENT_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => onSelectTab(t.id)}
              className={`relative flex-1 md:flex-none md:w-40 rounded-lg py-2 flex flex-col items-center gap-0.5 ${parentTab === t.id ? "bg-pill text-navy" : "text-slate-400"}`}
            >
              {/* aria-hidden on the icon (unlike ChildView's own bottom nav, whose Home
                  button deliberately leaves its emoji in the accessible name) — keeps each
                  button's accessible name exactly its label ("Home"/"Kids"/"Requests"),
                  matching the old pill bar's plain-text buttons so existing `exact: true`
                  nav-path assertions keep working unchanged. */}
              <span className="leading-none" aria-hidden="true"><Icon id={t.icon} size={20} style={{ color: parentTab === t.id ? "currentColor" : t.tone }} /></span>
              <span className="text-xs font-semibold">{t.label}</span>
              {t.id === "requests" && pendingCount > 0 && (
                <span className="absolute top-0.5 right-2 bg-rose-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  ), document.body);
}

export function App({ state, update, updateAndSave, code, showToast, setPendingVerifiedPin, setPendingVerifiedPasscode, setPendingVerifiedJoinToken, justCreated, onLeaveHousehold, offline, onSoftRefresh }) {
  // LB-366: the test-history writer (see makeHistoryWriter) — one per App mount, so the record
  // a settlement writes and the reward stamp that follows it are paired by the same object.
  const historyWriterRef = useRef(null);
  // Word bank flush (TTS_SCOPE.md §5.3.1). Alan, 29 Sep: "I want the audio to auto generate
  // when a word is added to a custom list or to a homework list." Words used to wait for the
  // NEXT app open (to decorrelate submission timing from typing) and then for a moderator; now
  // they go up WORDBANK_FLUSH_DELAY_MS after the queue last changed, the server approves what
  // passes the rude-word filter and voices it straight away (worker/lib/tts-wordbank.js). The
  // pause gathers a burst of adds into one request instead of one per word.
  //
  // Still household-agnostic: no household code is read out of `state` for this call — only
  // the bare word strings. Nothing is sent while offline, so a word is never cleared before it
  // has actually gone; what did not fit this flush (wordbankFlushPlan) stays queued, and only
  // the words that were SENT are removed, so a word queued in the meantime is kept.
  const pendingWords = state.pendingWordbankSubmissions;
  // LB-373 (29 Sep): the timer must restart only when the QUEUE changes. update() deep-clones
  // state, so `pendingWords` is a new array after every save even when nothing was queued —
  // keyed on the array, a device busy with a game kept pushing the flush back. Keyed on the
  // contents, an unrelated save leaves the 4 s countdown running.
  const pendingWordsRef = useRef(pendingWords); pendingWordsRef.current = pendingWords;
  const pendingWordsKey = Array.isArray(pendingWords) ? pendingWords.join("\n") : "";
  useEffect(() => {
    if (offline || !pendingWordsKey) return undefined;
    const t = setTimeout(() => {
      const { batches } = wordbankFlushPlan(pendingWordsRef.current);
      if (!batches.length) return;
      for (const batch of batches) apiSubmitWordbank(batch);
      const sent = new Set(batches.flat());
      // `auto`: a queue flush is machine-initiated (see Root.update) — it must not mark the
      // device as user-dirty for offline reconnect purposes.
      update((s) => { s.pendingWordbankSubmissions = (s.pendingWordbankSubmissions || []).filter((w) => !sent.has(String(w).trim())); return s; }, { auto: true });
    }, WORDBANK_FLUSH_DELAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingWordsKey, offline]);

  // What this device said it was, when it joined (see DEVICE_ROLE_KEY_PREFIX). null on any
  // device that has never been asked: every device that joined before this existed, and the
  // household creator's own, which goes through the wizard instead.
  const [deviceRole, setDeviceRole] = useState(() => {
    try {
      const raw = localStorage.getItem(DEVICE_ROLE_KEY_PREFIX + code);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // The device that just created this household opens straight into Parent Mode with no PIN,
  // to walk straight into the guided setup wizard below. A device that told us it belongs to
  // a grown-up also opens in Parent Mode (Alan, 30 Aug 2026) — kid-first boot is right for a
  // child's device and a shared one, and a daily annoyance on a parent's second device.
  //
  // This does NOT weaken the PIN gate: opening in Parent Mode still renders the PinPad
  // exactly as tapping the Parent Mode button does (see the pinPrompt effect below), and the
  // role can only be set to "parent" by someone who proved the PIN during device setup. It
  // changes which screen you land on, not who can read it.
  // Mid-setup reload (LB-127 PR3 smoke, Alan, 2 Sep 2026): a household that has not finished
  // the wizard boots into Parent Mode too. Before this, a reload half-way through setup —
  // the creating device has no device role yet, because that prompt is deferred until the
  // wizard is done — landed in Kid Mode on child 1, showing a child a half-built household
  // while the parent hunted for where setup had gone. The wizard is the only thing this
  // household can usefully do until it is finished, so that is where a reload goes: same
  // PIN posture as the parent-role boot (the pad is armed below once a real PIN exists).
  // Only on the device that CREATED the household (CREATOR_KEY_PREFIX, set by Root at
  // create/restore). A device that merely joined an unfinished household — a kid's tablet
  // scanning the QR while the parent is still on step 3 — keeps its kid-first boot and the
  // "ask a grown-up" card; it has no marker, and the parent's own phone is where setup
  // lives. Found by CI on PR4: specs 01 and 18 join an unfinished household from a second
  // context and expect exactly that.
  const createdHere = (() => { try { return localStorage.getItem(CREATOR_KEY_PREFIX + code) === "1"; } catch { return false; } })();
  const setupUnfinished = !state.onboardingDone && createdHere && deviceRole?.kind !== "child";
  // The floating Kid Mode button only once the header's own has scrolled away (Alan,
  // 4 Sep): two identical CTAs on one screen read as a mistake. IntersectionObserver where
  // it exists; where it does not (jsdom), the FAB simply shows, as before.
  const headerKidModeRef = useRef(null);
  const [headerKidModeVisible, setHeaderKidModeVisible] = useState(false);
  const softKeyboardOpen = useSoftKeyboardOpen();
  const photoStepOpen = usePhotoStepOpen();
  const [mode, setMode] = useState(justCreated || setupUnfinished || deviceRole?.kind === "parent" ? "parent" : "child");
  // Arming the PIN gate for the boot above. `mode` alone renders Parent Mode; the PinPad is
  // a SEPARATE piece of state that only the Parent Mode button and the deep links set, so
  // booting into Parent Mode without this would have been a way into the parent surface with
  // no PIN at all. Same posture as the ?goto= deep links below: pinConfigured households get
  // the pad, a household that has never set a real PIN doesn't (there is nothing to prove).
  // The grace window (LB-342, 20 Sep) is read HERE, at the one place the pad is armed on
  // boot, so a reload this tab performed — the install carry being the reason it exists —
  // does not re-ask for a PIN typed moments ago. It is a reason to SKIP the pad, never a
  // reason to raise Parent Mode: `mode` above is decided by the same rules as before, and a
  // device that would have booted into Kid Mode still does.
  const [pinPrompt, setPinPrompt] = useState(
    !justCreated
    && (setupUnfinished || deviceRole?.kind === "parent")
    && !!state.settings.pinConfigured
    && !parentUnlockedRecently(code)
  );
  const wizardActive = !state.onboardingDone;
  // Coming back to the app. The window does the deciding: a glance at a notification and
  // straight back is forgiven, a genuine absence is not. Only ever drops to Kid Mode and
  // re-arms the pad — never the reverse.
  useEffect(() => {
    if (!state.settings.pinConfigured) return undefined;
    const onVisible = () => {
      // LEAVING. The stamp is set when the PIN is typed, so on its own it measures
      // time-since-unlock — which evicts a parent who has been USING Parent Mode for six
      // minutes the moment they glance at a notification, an absence of ten seconds punished
      // as if it were ten minutes. Re-stamping as they leave makes the stamp mean "last seen",
      // so the window measures the GAP, which is the thing anybody actually cares about.
      //
      // Only ever EXTENDS an unlock: it needs a stamp already there, so a tab that has proved
      // nothing cannot acquire a window by being backgrounded, and one Kid Mode cleared cannot
      // get it back. And only in Parent Mode, because a phone handed over in Kid Mode is the
      // case the clearing exists for.
      if (document.visibilityState !== "visible") {
        if (mode === "parent" && parentUnlockStampedAt(code) !== null) markParentUnlocked(code);
        return;
      }
      // COMING BACK. Inside the window, re-stamp and carry on.
      if (parentUnlockedRecently(code)) { markParentUnlocked(code); return; }
      clearParentUnlocked(code);
      setMode((m) => (m === "parent" ? "child" : m));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [code, state.settings.pinConfigured, mode]);
  // Design pass A (Sep 2026): the August "brand-warm" cream body override is gone. Both
  // modes sit on the one canvas — the brand tint from src/styles/tokens.css, applied by
  // the stylesheet, never by an inline body style (an inline style beats the stylesheet,
  // which is exactly how the cream survived the first restyle). The one-off clear below
  // is so a session that still carries the old inline value drops it.
  useEffect(() => { document.body.style.backgroundColor = ""; }, []);
  // LB-152: <html data-mode> lets tokens.css scale kid mode at tablet width (root font-size)
  // without any component knowing about breakpoints. Cleared on unmount.
  useEffect(() => {
    // App's mode value is "child"; the design tokens use "kid" (matching ChildView's own
    // data-mode="kid" root), so map here rather than leak the internal name into CSS.
    document.documentElement.setAttribute("data-mode", mode === "parent" ? "parent" : "kid");
    return () => { document.documentElement.removeAttribute("data-mode"); };
  }, [mode]);
  const [pinError, setPinError] = useState("");
  const [pinChecking, setPinChecking] = useState(false);
  const [verifiedPin, setVerifiedPin] = useState(null);
  // LB-340 — WHICH adult proved that PIN. Session-only, alongside verifiedPin and for the
  // same reason: it is the answer to "who is in Parent Mode right now", and a new session
  // should ask again rather than assume. null means the Owner (an unclaimed device, or an
  // old client that never sent one), which is what the worker reads an absent id as. Part 2
  // hangs attribution off this; part 1 only needs it to prove the right PIN on a save.
  const [activeAdultId, setActiveAdultId] = useState(null);

  // LB-338 (C2): the Owner's first name, on a household made before LB-337 made it mandatory
  // on the create form. Only the OWNER is asked — an Adult cannot set somebody else's name,
  // and blocking them on a field they cannot fill would be a wall with no door. The condition
  // IS the "once": it renders while the Owner has no name, so saving one ends it for good.
  // No seen-flag, so nothing can clear one and make it reappear on a household that answered.
  const ownerRow = ownerOf(state);
  const needsOwnerName = mode === "parent"
    && !!ownerRow
    && !String(ownerRow.name || "").trim()
    && !!activeAdultId
    && activeAdultId === ownerRow.id;
  // Restore the child this device was last on, rather than always snapping to the first
  // profile. Validated against the CURRENT profile list on read: a stored id whose child
  // has since been deleted, or was never in this household, falls back to profiles[0]
  // exactly as before. A reload should not silently move a parent — or a child on a
  // shared tablet — to somebody else's profile.
  const [activeChildId, setActiveChildId] = useState(() => {
    const fallback = state.profiles[0]?.id ?? null;
    try {
      const saved = localStorage.getItem(ACTIVE_CHILD_KEY_PREFIX + code);
      if (saved && state.profiles.some((p) => p.id === saved)) return saved;
    } catch {}
    return fallback;
  });
  // The Parent Mode tab. Alan reversed the old rule on 2 Oct: it used to be restored on every
  // entry ("unlocking lands you where you were instead of on Home"), so a parent who last left
  // from Settings unlocked into Settings, which read as the app having gone somewhere on its
  // own. Now entering Parent Mode FROM KID MODE always opens Home (see the mode effect below,
  // the one place that decides it); a deep link keeps its destination (a requests push lands on
  // Requests, the set-up link in the wizard). The stored tab is read only here, by a boot that
  // lands straight in Parent Mode, which is a reload while already there: that stays put. It
  // never opens Parent Mode by itself, so the PIN gate is untouched.
  const [parentTab, setParentTab] = useState(() => {
    try {
      const saved = localStorage.getItem(PARENT_TAB_KEY_PREFIX + code);
      if (saved && ["home", "kids", "requests", "settings"].includes(saved)) return saved;
    } catch {}
    return "home";
  });
  useEffect(() => {
    const el = headerKidModeRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setHeaderKidModeVisible(false); return; }
    const io = new IntersectionObserver(([entry]) => setHeaderKidModeVisible(entry.isIntersecting), { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
    // wizardActive is a dependency because the header's Kid Mode button does not exist while
    // the wizard is unfinished: without it the ref is null on this effect's only run, no
    // observer is ever created, headerKidModeVisible stays false, and the FAB would render
    // alongside the header button the moment setup completed.
  }, [mode, parentTab, wizardActive]);
  // Unsaved-changes guard (Aug 2026). ParentView keeps this pointed at ProfilesTab's
  // imperative handle (hasUnsavedChanges/trySave/discard) whenever the Kids tab is the one
  // showing, and clears it otherwise — see ParentView's own comment on unsavedGuardRef.
  // A plain ref, not state: nothing here should ever trigger a re-render on its own, it's
  // only ever read at the moment of an attempted navigation.
  const unsavedGuardRef = useRef(null);
  // Set only while the unsaved-changes dialog itself is open — holds the navigation the
  // parent was trying to make (switch tab / exit to Kid Mode / open Settings) so it can be
  // resumed after Save or Discard, or simply dropped if they choose to keep editing.
  const [pendingNav, setPendingNav] = useState(null);
  // Every direct navigation trigger in this header/nav (bottom-bar tab buttons, the Kid Mode
  // button, the Settings gear) should route through this rather than calling
  // setParentTab/setMode straight, so a parent can never silently lose an in-progress child
  // edit just by tapping away. Deliberately NOT applied to the deep-link handlers further
  // down (a push-notification tap landing on /?goto=requests) — those are a rarer,
  // externally-triggered path or landing on a fresh state at that point in most flows; a
  // parent actively mid-edit on the Kids tab when one arrives is an accepted, low-stakes gap
  // rather than something worth complicating those handlers for.
  function attemptNav(action) {
    const guard = unsavedGuardRef.current;
    if (guard && guard.hasUnsavedChanges()) {
      setPendingNav(() => action);
      return;
    }
    action();
  }
  // Shown once, right when the setup wizard's final step is finished — see onFinished below.
  const [showSetupComplete, setShowSetupComplete] = useState(false);
  // Lifted up from ChildView so HeroCard can render inside the same frozen top region as the
  // chip row (see the sticky wrapper below) instead of scrolling away with the rest of the tab
  // content. Persists across switching which child is active, matching prior behaviour.
  const [childTab, setChildTab] = useState("home");

  // LB-147: the side-by-side CMS drives the preview to a screen before highlighting a string.
  // Registered on window so the CMS page can call it through the iframe; armed ONLY in the CMS
  // preview, so nothing is exposed in a real session. Mode and tab are the two axes the screen
  // list records — anything reached by a tap inside a screen is a later pass, and a screen with
  // no route simply lists its strings.
  useEffect(() => {
    if (typeof window === "undefined" || !window.__LB_CMS_PREVIEW__) return;
    window.__lbCmsNavigate = (route) => {
      if (!route || typeof route !== "object") return false;
      if (route.mode === "parent" || route.mode === "child") setMode(route.mode);
      if (route.mode === "parent" && route.tab) setParentTab(route.tab);
      if (route.mode === "child" && route.tab) setChildTab(route.tab);
      return true;
    };
    return () => { try { delete window.__lbCmsNavigate; } catch { /* nothing to clean */ } };
  }, []);
  // Kid-mode pinned region (child row + hero): see the sticky wrapper below. It used to
  // track "resting vs stuck" with a 1px sentinel + IntersectionObserver so it could pad
  // itself down to the physical top edge only once stuck. That detection failed twice on
  // iPhone (7 Sep: false-stuck at scroll 0; 8 Sep, prod: never-stuck once scrolled), and each
  // failure leaves the safe-area band above the bar transparent, so scrolled content shows
  // through it beside the Dynamic Island. Now there is no state to get wrong: a fixed,
  // canvas-coloured backdrop covers that band whenever the pinned region is mounted, and the
  // region itself always sits just below it.
  // True only when the kid arrived at Earn by tapping Home's streak card — tells Earn to open
  // Daily Tasks pre-expanded. Cleared by the bottom nav's own Earn button so a later, unrelated
  // visit to Earn doesn't also force it open.
  const [earnFocusTasks, setEarnFocusTasks] = useState(false);
  // Same idea as earnFocusTasks above, but for the Learn & Earn teaser on Home — true only
  // when the kid arrived at Earn by tapping "Brain boost time!", so EarnTab knows to scroll
  // the Learn & Earn section into view rather than landing at the top. Cleared by the
  // bottom nav's own tab buttons, same as earnFocusTasks.
  const [earnFocusLearn, setEarnFocusLearn] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  // Help & support (docs/HELP_SCOPE.md). `helpOpen` carries how it was opened, because
  // the sheet behaves differently per entry point: the header ❓ and the footer link land
  // on the FAQ, the error strip below lands straight on the report form with the error
  // already attached. Null means closed.
  const [helpOpen, setHelpOpen] = useState(null); // null | { view, errorHint }
  const openHelp = useCallback((opts) => setHelpOpen({ view: "faq", errorHint: "", ...(opts || {}) }), []);
  // The one entry point that reaches people who would otherwise just close the tab. An
  // uncaught error or unhandled rejection has already been counted as an app_error; this
  // is the human half — say something happened, and offer a one-tap way to tell us what
  // they were doing, which is the part no telemetry can reconstruct. Dismissible, and
  // shown at most once per load so an error loop can't pin a banner to the screen.
  const [errorStrip, setErrorStrip] = useState(null);
  const errorStripShownRef = useRef(false);
  useEffect(() => onAppError((message) => {
    if (errorStripShownRef.current) return;
    errorStripShownRef.current = true;
    setErrorStrip(message);
  }), []);
  // Jump back to the top of the page whenever the visible tab changes (Home/Earn/Rewards in
  // kid mode, Rewards/Requests/Household/Learn & Earn in parent mode) — otherwise switching
  // tabs mid-scroll leaves the new tab's content starting wherever the old scroll position was.
  // Skipped when earnFocusLearn is driving this tab change — that flow scrolls straight to the
  // Learn & Earn section itself (see EarnTab's own scrollIntoView effect), and this effect
  // fires in the same render pass and would otherwise win, snapping back to the top a moment
  // after the teaser just scrolled down.
  useEffect(() => { if (!earnFocusLearn) window.scrollTo({ top: 0, behavior: "smooth" }); }, [childTab, parentTab]);
  // A parent tapping a "new request" push notification lands here via /?goto=requests — auto-open
  // the PIN prompt so they don't need an extra tap, and once they're in, jump straight to Requests.
  // A ref rather than state: the postMessage handler below needs to set this *after* mount too
  // (an already-open background tab getting a notificationclick postMessage), not just read it
  // once at mount like the effect further down does.
  const deepLinkToRequestsRef = useRef(new URLSearchParams(window.location.search).get("goto") === "requests");
  // Owner-verification return link (28 Aug, staging test B2): /?goto=setup, sent by the
  // "Back to Little Bankers →" CTA on the verification confirmation page. A parent who has
  // just confirmed their email is mid-first-run by definition, and `mode` below defaults to
  // "child" on every load — so they were landing in Kid Mode reading "a parent needs to set
  // this up", with nothing to tell them they were the parent. Deliberately NOT a change to
  // the mode default: kid-first boot is correct on a shared family device, and flipping it
  // would alter every other entry path three days before friends & family.
  const deepLinkToSetupRef = useRef(new URLSearchParams(window.location.search).get("goto") === "setup");
  const [hiddenIds, setHiddenIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HIDDEN_CHILDREN_KEY_PREFIX + code) || "[]"); } catch { return []; }
  });
  // Per-device, per-child notification mute — same shape/pattern as hiddenIds. See
  // NotificationToggle's forcedOff prop (ChildView) and ProfilesTab's matching toggle.
  // Hiding a child always forces them into this list too (see toggleChildVisibility).
  const [mutedIds, setMutedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MUTED_CHILDREN_KEY_PREFIX + code) || "[]"); } catch { return []; }
  });
  // Drives the three-screen device setup flow for a device that just joined an established
  // multi-kid household: "intro" -> "pin" -> "kids" -> null (done). Reads (and immediately
  // clears) the flag Root.jsx's handleJoin set, so this can only ever start once per join —
  // a reload mid-flow lands the device in normal Kid Mode rather than restarting it.
  const [deviceSetupStep, setDeviceSetupStep] = useState(() => {
    try {
      const pending = localStorage.getItem(PENDING_DEVICE_SETUP_KEY_PREFIX + code) === "1";
      if (pending) localStorage.removeItem(PENDING_DEVICE_SETUP_KEY_PREFIX + code);
      // The profiles.length > 1 condition is gone (Alan, 30 Aug 2026) — see the constants
      // comment on PENDING_DEVICE_SETUP_KEY_PREFIX. One child still means one device that
      // is either theirs, the family's, or a grown-up's.
      if (pending) return "whose";
    } catch {}
    return null;
  });
  const [deviceSetupPinError, setDeviceSetupPinError] = useState("");
  // The answer being confirmed, held between the "whose" and "confirm" screens. Nothing is
  // written anywhere until the parent (or child) confirms it.
  const [pendingDeviceRole, setPendingDeviceRole] = useState(null);

  const profiles = state.profiles;
  const visibleProfiles = profiles.filter((p) => !hiddenIds.includes(p.id));
  const activeChild = profiles.find((p) => p.id === activeChildId) || profiles[0];

  // ---- W2.5 screen telemetry (docs/ANALYTICS_SCOPE.md §3.1 Screens) ----
  // Mode is a dimension stamped onto every event rather than an event of its own.
  useEffect(() => {
    setMetricsMode(mode === "parent" ? "parent" : "kid");
  }, [mode]);
  useEffect(() => {
    setMetricsBand(ageBand(activeChild));
  }, [activeChild?.id, activeChild?.age]);
  // One screen_view per tab landing, kid and parent sides. The wizard tracks its own
  // steps (Wizard.jsx) and the focus windows ride on the tab they open from — v1 keeps
  // screen ids to the surfaces the dashboard's funnel views actually read.
  useEffect(() => {
    if (mode === "child") {
      setMetricsScreen("kid:" + childTab);
      track("screen_view", { screen: "kid:" + childTab, band: ageBand(activeChild) || undefined });
    }
  }, [mode, childTab]);
  useEffect(() => {
    if (mode === "parent" && !wizardActive) {
      setMetricsScreen("parent:" + parentTab);
      track("screen_view", { screen: "parent:" + parentTab });
    }
  }, [mode, parentTab, wizardActive]);

  useEffect(() => {
    if (!activeChild && profiles.length > 0) setActiveChildId(profiles[0].id);
  }, [profiles]);

  // Persist "where this device was" so a reload restores it (see the useState
  // initialisers). Per device, never synced — two devices in a household are usually two
  // people looking at two different children. Writes are cheap and wrapped: a browser with
  // storage disabled just loses the convenience, it must never break navigation.
  useEffect(() => {
    if (!code || !activeChildId) return;
    try { localStorage.setItem(ACTIVE_CHILD_KEY_PREFIX + code, activeChildId); } catch {}
  }, [code, activeChildId]);
  useEffect(() => {
    if (!code || !parentTab) return;
    try { localStorage.setItem(PARENT_TAB_KEY_PREFIX + code, parentTab); } catch {}
  }, [code, parentTab]);

  useEffect(() => {
    if (deepLinkToRequestsRef.current) {
      if (state.settings.pinConfigured) {
        setPinPrompt(true);
      } else {
        // No real PIN set yet — Parent Mode is open, so just land there directly.
        setMode("parent");
        setParentTab("requests");
      }
      try { window.history.replaceState(null, "", window.location.pathname); } catch {}
    }
    if (deepLinkToSetupRef.current) {
      // Same PIN posture as the requests link above, for the same reason: a household that
      // has already set a real PIN gets the prompt, so this can never be a way into Parent
      // Mode without one. A brand-new owner (the case this exists for) has pinConfigured
      // false, so they land straight in the wizard — App renders it whenever
      // onboardingDone is false and mode is parent.
      if (state.settings.pinConfigured) {
        setPinPrompt(true);
      } else {
        setMode("parent");
      }
      try { window.history.replaceState(null, "", window.location.pathname); } catch {}
    }
  }, []);

  // Covers the case client.navigate() misses in sw.js: an already-open background tab gets
  // told via postMessage instead of a real navigation, so this has to do the same routing
  // the mount effect above does, just triggered later instead of at load time.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    function handleMessage(event) {
      if (event.data && event.data.type === "lb-notification-click" && event.data.url) {
        const params = new URLSearchParams(new URL(event.data.url, window.location.origin).search);
        if (params.get("goto") !== "requests") return;
        deepLinkToRequestsRef.current = true;
        if (state.settings.pinConfigured) {
          setPinError("");
          setPinPrompt(true);
        } else {
          setMode("parent");
          setParentTab("requests");
        }
      }
    }
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, [state.settings.pinConfigured]);
  useEffect(() => {
    // On a device where the active child has been hidden, don't leave a child stuck viewing a
    // profile a parent hid on purpose — fall back to the first profile still visible here.
    if (mode !== "child" || !activeChild) return;
    if (hiddenIds.includes(activeChild.id) && visibleProfiles.length > 0) setActiveChildId(visibleProfiles[0].id);
  }, [mode, hiddenIds, activeChild]);

  function toggleChildVisibility(childId) {
    const wasHidden = hiddenIds.includes(childId);
    setHiddenIds((prev) => {
      const next = wasHidden ? prev.filter((id) => id !== childId) : [...prev, childId];
      try { localStorage.setItem(HIDDEN_CHILDREN_KEY_PREFIX + code, JSON.stringify(next)); } catch {}
      return next;
    });
    // Visibility and notifications are now kept in lockstep on this device: hiding a child
    // forces their notifications off (no point notifying about a kid who isn't even shown
    // here), and showing them again turns notifications back on automatically — a parent
    // who un-hides a child clearly wants to see them again, and that should include being
    // notified about them, not a silent gap they'd have to remember to fix in Settings.
    setMutedIds((prev) => {
      const isMuted = prev.includes(childId);
      let next;
      if (!wasHidden) {
        // Being hidden now — mute if not already muted.
        if (isMuted) return prev;
        next = [...prev, childId];
      } else {
        // Being shown again now — unmute if currently muted.
        if (!isMuted) return prev;
        next = prev.filter((id) => id !== childId);
      }
      try { localStorage.setItem(MUTED_CHILDREN_KEY_PREFIX + code, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  /* Writes a whole device role at once, rather than toggling child by child (Alan, 30 Aug
   * 2026). Everything here is per-device localStorage — none of it touches household state,
   * so one family member's answer can never overwrite another's.
   *
   * Visibility and mute move together, matching toggleChildVisibility's existing rule: a
   * child who isn't shown on this device can't meaningfully have notifications on it. Note
   * what this does NOT do — it never subscribes or unsubscribes anything. mutedIds gates the
   * prompt and the toggle; the only thing that creates a push subscription is
   * requestPushSubscription, and ParentNotificationPrompt (held back until this flow closes)
   * is what asks. That separation is deliberate: a refused browser permission is close to
   * unrecoverable, so it gets its own moment rather than riding inside a summary screen.
   */
  function applyDeviceRole(role) {
    const hidden = role.kind === "child" ? profiles.filter((p) => p.id !== role.childId).map((p) => p.id) : [];
    setHiddenIds(hidden);
    setMutedIds(hidden);
    setDeviceRole(role);
    try {
      localStorage.setItem(HIDDEN_CHILDREN_KEY_PREFIX + code, JSON.stringify(hidden));
      localStorage.setItem(MUTED_CHILDREN_KEY_PREFIX + code, JSON.stringify(hidden));
      localStorage.setItem(DEVICE_ROLE_KEY_PREFIX + code, JSON.stringify(role));
      // WHICH grown-up, not just "a grown-up". adultForDevice reads this to decide whose PIN
      // the pad asks for; without it a second parent's phone opened Parent Mode asking for
      // the Owner's. It names nobody and grants nothing — the PIN still has to be right, and
      // verify-pin re-checks server-side and records the real answer on the device record.
      if (role.kind === "parent" && role.adultId) setDeviceOwnerAdultId(code, role.adultId);
      // Everyone present when the role was chosen counts as seen — the new-child prompt is
      // for arrivals AFTER this point, not for the household as it stands right now.
      localStorage.setItem(SEEN_CHILDREN_KEY_PREFIX + code, JSON.stringify(profiles.map((p) => p.id)));
    } catch {}
    if (role.kind === "child") setActiveChildId(role.childId);
  }

  function deviceRoleLabel(role) {
    if (!role) return "";
    if (role.kind === "parent") {
      const named = role.adultId ? (state.adults || []).find((a) => a && a.id === role.adultId) : null;
      const label = named ? adultLabel(named) : "";
      return label ? ct("device.role.adult", "{name}'s device", { name: label }) : ct("device.role.parent", "a grown-up's device");
    }
    if (role.kind === "shared") return ct("device.role.shared", "a shared family device");
    const child = profiles.find((p) => p.id === role.childId);
    return child ? ct("device.role.child", "{name}'s device", { name: child.name }) : ct("device.role.shared", "a shared family device");
  }

  function toggleChildMute(childId) {
    // A hidden child's notifications can only ever be off on this device — this mirrors
    // the disabled toggle in the UI as a safety net in case a stale UI somehow lets a tap
    // through.
    if (hiddenIds.includes(childId)) return;
    setMutedIds((prev) => {
      const next = prev.includes(childId) ? prev.filter((id) => id !== childId) : [...prev, childId];
      try { localStorage.setItem(MUTED_CHILDREN_KEY_PREFIX + code, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  // Screen 2 of the device setup flow (PinPad) calls this on submit. Success moves to
  // screen 3 and — just like the normal header PIN check — records the verified PIN so
  // any background save made while this flow is still open can prove it to the server.
  async function submitDeviceSetupPin(digits, adultId) {
    try {
      const res = await apiVerifyPin(code, digits, adultId);
      const ok = res.valid;
      if (ok) {
        setVerifiedPin(digits);
        // The adult the WORKER proved, not the one we asked about. On a device minted before
        // part 1 there is no stored ownerAdultId, so `adultId` is null and the worker resolves
        // it to the Owner — recording our own null here left Parent Mode unlocked with nobody
        // on the session, which is why the Adults card hid its Invite row on exactly the
        // devices most likely to be testing it.
        const proved = res.adultId || adultId || null;
        setActiveAdultId(proved);
        // LB-343: mirrored into the session so every write site can attribute without a prop.
        setSessionAdultId(code, proved);
        markParentUnlocked(code); // LB-342: the other unlock site, same window
        // …and the device learns whose it is, so the next unlock does not have to ask.
        if (proved) setDeviceOwnerAdultId(code, proved);
        setDeviceSetupPinError("");
        ensureProvenDevice(code, digits, adultId); // LB-178: the PIN just typed is this device's proof
        // The PIN is now asked only where it actually guards something (Alan, 30 Aug 2026).
        // It used to sit between the intro and the toggles, which meant a child who scanned
        // a quick-join QR hit a wall they couldn't pass and skipped — landing on the least
        // private setup by the most likely route. Choosing a child's or a shared device is
        // safe for anyone to do: it can only ever narrow what this device shows, and skipping
        // already showed everything. "A grown-up's device" is different, because it opens the
        // parent surface on every boot, so that one still proves the PIN.
        if (pendingDeviceRole) {
          applyDeviceRole(pendingDeviceRole);
          setPendingDeviceRole(null);
          finishDeviceSetup(true);
          return;
        }
        setDeviceSetupStep("kids");
      } else {
        setDeviceSetupPinError("Incorrect PIN — try again");
      }
    } catch (err) {
      // LB-177: a lockout carries its own wait-time message from the worker.
      setDeviceSetupPinError(err?.rateLimited ? err.message : "Couldn't check the PIN — check your connection and try again");
    }
  }

  // Ends the device setup flow — from either exit point (screen 1's skip, or screen 3's
  // save). Only the "save" path (a verified parent) lands in Parent Mode; skipping leaves
  // the device in its normal Kid Mode landing spot.
  function finishDeviceSetup(enterParentMode) {
    setDeviceSetupStep(null);
    setPendingDeviceRole(null);
    if (enterParentMode) setMode("parent");
  }

  // Confirming an answer. Child and shared devices apply straight away and land in Kid Mode;
  // a grown-up's device goes through the PIN first (see submitDeviceSetupPin above).
  function confirmDeviceRole() {
    if (!pendingDeviceRole) return;
    if (pendingDeviceRole.kind === "parent") { setDeviceSetupStep("pin"); return; }
    applyDeviceRole(pendingDeviceRole);
    setPendingDeviceRole(null);
    finishDeviceSetup(false);
  }

  /* A child who joined the household AFTER this device was set up.
   *
   * Everything present at setup time was recorded then (applyDeviceRole), and every device
   * seeds the list silently on its first read, so this only ever fires for a genuine new
   * arrival. Devices that were never asked — the creator's own, anything that joined before
   * this shipped — record and stay quiet: they have no stated role to contradict, and
   * "everyone is visible" is what they already expected. */
  const [newChildPrompt, setNewChildPrompt] = useState(null);
  useEffect(() => {
    if (!code || !profiles.length) return;
    if (deviceSetupStep !== null || wizardActive) return;
    let seen;
    try { seen = JSON.parse(localStorage.getItem(SEEN_CHILDREN_KEY_PREFIX + code) || "null"); } catch { seen = null; }
    const ids = profiles.map((p) => p.id);
    const record = () => { try { localStorage.setItem(SEEN_CHILDREN_KEY_PREFIX + code, JSON.stringify(ids)); } catch {} };
    if (!Array.isArray(seen)) { record(); return; }   // first read on this device: seed, never prompt
    const fresh = profiles.filter((p) => !seen.includes(p.id));
    if (!fresh.length) return;
    if (!deviceRole || deviceRole.kind !== "child") { record(); return; } // nothing to contradict
    if (!newChildPrompt) setNewChildPrompt(fresh[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, profiles.length, deviceSetupStep, wizardActive, deviceRole]);

  function resolveNewChild(show) {
    const child = newChildPrompt;
    setNewChildPrompt(null);
    if (!child) return;
    if (show) toggleChildVisibility(child.id);   // un-hides and un-mutes together
    try { localStorage.setItem(SEEN_CHILDREN_KEY_PREFIX + code, JSON.stringify(profiles.map((p) => p.id))); } catch {}
  }

  // LB-366 — the writes into learningContent[childId].testHistory (src/lib/testHistory.js).
  const stateForHistoryRef = useRef(state); stateForHistoryRef.current = state;
  if (!historyWriterRef.current) historyWriterRef.current = makeHistoryWriter({ update, coachOn: () => coachEnabled(stateForHistoryRef.current.settings), makeId: uid, now: nowIso });
  const recordHistory = (childId, game, opts, abandoned) => historyWriterRef.current.record(childId, game, opts, abandoned);
  const stampHistoryReward = (kind, amount, sent, requestId = null) => historyWriterRef.current.stamp(activeChild?.id, kind, amount, sent, requestId);

  function updateChildAvatar(childId, patch) {
    update((s) => {
      const child = s.profiles.find((p) => p.id === childId);
      if (!child) return s;
      // LB-168: `characters` is nested (owned + equipped) — merge it rather than replace it.
      const { characters, ...rest } = patch || {};
      Object.assign(child, rest);
      if (characters) child.characters = { ...(child.characters || { owned: [], equipped: null }), ...characters };
      return s;
    });
  }

  // balance: "token" (default) adjusts child.tokenBalance in tokens, "vault" adjusts
  // child.vaultBalancePence in pence. Both are logged as one transaction row either way.
  // `extra`, when passed, is spread onto the transaction row as-is — used to tag data at
  // write time rather than reconstruct it later, e.g. { earnCap: true } marks a credit as
  // counting toward the child's weekly spending cap (see earnedThisWeekPence).
  function addTransaction(childId, amount, reason, type, balance = "token", requestId, extra) {
    if (!amount || amount <= 0 || !reason.trim()) return false;
    update((s) => {
      // LB-343: deviceId answers "which phone", which on a shared family phone is not the
      // question a child is asking. adultId names the person and adultName copies their name
      // in at write time, so removing them later cannot un-name what they did.
      s.transactions.unshift(stampActor({ id: uid(), deviceId: DEVICE_ID, childId, amount, reason: reason.trim(), type, balance, timestamp: nowIso(), ...(requestId ? { requestId } : {}), ...(extra || {}) }, actor(s)));
      const p = s.profiles.find((p) => p.id === childId);
      if (p) {
        const delta = type === "credit" ? amount : -amount;
        if (balance === "vault") p.vaultBalancePence = (p.vaultBalancePence || 0) + delta;
        else p.tokenBalance += delta;
      }
      return s;
    });
    const name = profiles.find((p) => p.id === childId)?.name ?? "Child";
    if (balance === "vault") {
      if (type === "spend") {
        showToast(ct("toast.child_spent", "{name} spent {amount}", { name, amount: fmtPence(amount) }), "good");
      } else {
        const verb = type === "credit" ? "gained" : "lost";
        showToast(
          ct("toast.savings_move", "{name} {verb} {amount} in the Savings Vault", { name, verb, amount: fmtPence(amount) }),
          type === "debit" ? "bad" : "good"
        );
      }
    } else {
      const verb = type === "credit" ? "earned" : type === "spend" ? "spent" : "lost";
      const tone = type === "debit" ? "bad" : "good";
      showToast(ct("toast.tokens_move", "{name} {verb} {amount} tokens", { name, verb, amount }), tone);
    }
    return true;
  }

  // Moves tokens out of the token balance and the equivalent £ into the bank balance in one
  // atomic update, logging both sides as linked transaction rows with a single toast.
  // Each row also carries `vaultTransfer` (direction) plus the linked tokens/pence figures,
  // so the Savings Vault Transactions list can render "Move £x (y tokens)" without having to
  // reconstruct the pairing from two separately-timestamped rows.
  // `status`, when passed, is stamped onto both linked rows — used to log a transfer as
  // "pending" the moment a child sends it (see submitVaultTransfer below), so the balances
  // move immediately but the rows still read as awaiting a parent's confirmation. Left
  // undefined for the old direct-parent-move call sites, which stay immediately resolved
  // exactly as before.
  function moveTokensToVault(childId, tokens, pence, requestId, status) {
    // Both halves of a transfer share one groupId — that's what lets reverseTransaction
    // undo a transfer from EITHER side (the token-debit row or the Savings Vault Transactions
    // list's bank-credit row) and still flip both rows together, since a transfer isn't real
    // unless both sides move in lockstep.
    const groupId = uid();
    update((s) => {
      const p = s.profiles.find((p) => p.id === childId);
      if (!p) return s;
      p.tokenBalance -= tokens;
      p.vaultBalancePence = (p.vaultBalancePence || 0) + pence;
      s.transactions.unshift({ id: uid(), deviceId: DEVICE_ID, childId, amount: tokens, reason: "Moved to Savings Vault", type: "debit", balance: "token", timestamp: nowIso(), vaultTransfer: "to", tokens, pence, groupId, ...(requestId ? { requestId } : {}), ...(status ? { status } : {}) });
      s.transactions.unshift({ id: uid(), deviceId: DEVICE_ID, childId, amount: pence, reason: "Moved from tokens", type: "credit", balance: "vault", timestamp: nowIso(), vaultTransfer: "to", tokens, pence, groupId, ...(requestId ? { requestId } : {}), ...(status ? { status } : {}) });
      return s;
    });
    // A "pending" move gets its own dedicated feedback (the kid-side overlay, plus the
    // "sent to parent" toast in submitVaultTransfer) rather than this generic one, which is
    // written for an already-final, already-resolved move.
    if (status !== "pending") {
      const name = profiles.find((p) => p.id === childId)?.name ?? "Child";
      showToast(ct("toast.moved_to_vault", "{name} moved {tokens} tokens ({pence}) to the Savings Vault", { name, tokens, pence: fmtPence(pence) }), "good");
    }
  }

  // Shared low-level undo step: flips one or more already-written transaction rows (a group
  // is more than one row only for a moveTokensToVault pair — see groupId above) and logs a
  // matching "reversal" row for each, with the balance delta reversed. Deliberately allows
  // balances to go negative — a parent undoing something after the tokens/money have already
  // been spent elsewhere is a real scenario here, not an error case to block.
  function reverseTransactionRows(rows) {
    update((s) => {
      rows.forEach((row) => {
        const stRow = s.transactions.find((t) => t.id === row.id);
        if (!stRow || stRow.reversed || stRow.reversalOf) return;
        // LB-356: Oinks are not money, and everything below this moves money: anything other
        // than vault used to fall through to tokenBalance, so undoing a 50-Oink gift took 50
        // TOKENS. An Oinks gift is reversed by its own rule (lib/oinks.js), in Oinks, and
        // never reaches the money path.
        if (stRow.balance === "oinks") { reverseOinkGift(s, stRow, { newId: uid, deviceId: DEVICE_ID, nowIso: nowIso() }); return; }
        stRow.reversed = true;
        const reverseType = stRow.type === "credit" ? "debit" : "credit";
        const delta = reverseType === "credit" ? stRow.amount : -stRow.amount;
        const p = s.profiles.find((pp) => pp.id === stRow.childId);
        if (p) {
          if (stRow.balance === "vault") p.vaultBalancePence = (p.vaultBalancePence || 0) + delta;
          else p.tokenBalance += delta;
        }
        s.transactions.unshift({
          id: uid(),
          deviceId: DEVICE_ID,
          childId: stRow.childId,
          amount: stRow.amount,
          reason: stRow.reason,
          type: reverseType,
          balance: stRow.balance,
          timestamp: nowIso(),
          reversalOf: stRow.id,
          ...(stRow.vaultTransfer ? { vaultTransfer: stRow.vaultTransfer === "to" ? "from" : "to", tokens: stRow.tokens, pence: stRow.pence } : {}),
        });
        if (stRow.requestId) {
          const r = s.requests.find((rr) => rr.id === stRow.requestId);
          if (r && !r.reversed) { r.reversed = true; r.reversedAt = nowIso(); }
        }
      });
      return s;
    });
  }

  // Undo entry point for a single row shown in Recent Activity or Savings Vault Transactions
  // (Parent Mode only — see the row-level menu in ActivityRow). Pulls in the row's
  // moveTokensToVault sibling automatically via groupId so a transfer always reverses whole.
  function reverseTransaction(txId) {
    const original = state.transactions.find((t) => t.id === txId);
    if (!original || original.reversed || original.reversalOf) return;
    // LB-356: an Oinks gift the child has already spent in full has nothing to take back.
    // Said, rather than written as a reversal of zero.
    if (original.balance === "oinks" && !oinkGiftTakeBack(state, original)) {
      const name = profiles.find((p) => p.id === original.childId)?.name ?? "Child";
      showToast(ct("toast.oink_undo_nothing", "Nothing to take back. {name} has already spent those Oinks.", { name }), "info");
      return;
    }
    const group = original.groupId ? state.transactions.filter((t) => t.groupId === original.groupId) : [original];
    reverseTransactionRows(group);
    showToast(ct("toast.transaction_reversed", "Transaction reversed"), "info");
  }

  // Undo entry point for a resolved request in Recently Resolved. Reverses every transaction
  // row tagged with this request's id (a mixed goal redemption tags both its token-side and
  // Savings-Vault-side rows) so the whole approval unwinds together, then flags the request
  // itself so it reads as reversed wherever it's shown — Recently Resolved, and via
  // requestId on any linked row elsewhere. Redeemed goals are NOT restored to the child's
  // goal list; that's a deliberate scope decision since the goal may have since been edited
  // or re-added under a new id — only the token/money movement is undone.
  function reverseRequest(reqId) {
    const req = state.requests.find((r) => r.id === reqId);
    if (!req || req.status !== "approved" || req.reversed) return;
    const rows = state.transactions.filter((t) => t.requestId === reqId && !t.reversed && !t.reversalOf);
    reverseTransactionRows(rows);
    update((s) => {
      const r = s.requests.find((rr) => rr.id === reqId);
      if (r) { r.reversed = true; r.reversedAt = nowIso(); }
      return s;
    });
    showToast(ct("toast.request_reversed", "Request reversed"), "info");
  }

  // Puts back the balances from a Savings Vault transfer that's still "pending" — used both
  // when a parent rejects it (RequestsTab) and when the child cancels their own request before
  // a parent gets to it (cancelRequest below). submitVaultTransfer already moved both balances
  // the instant the transfer was sent, so undoing it here mirrors reverseTransactionRows'
  // balance-flip + offsetting-row logic, but deliberately does NOT use the generic "reversed"
  // flag: that flag means "this was approved, then later undone", which reads differently in
  // Recent Activity than "this was never approved in the first place". Instead the original
  // pair is stamped with `status: resultStatus` ("rejected" or "cancelled") so it reads
  // correctly everywhere status is already shown (kid Recent Activity / Transaction History,
  // parent Recent Activity, Savings Vault Transactions), while the new offsetting rows this
  // produces show up right alongside them as the reversing transaction.
  function rejectVaultTransfer(reqId, resultStatus) {
    update((s) => {
      // LB-343 (a): the adult doing the rejecting, resolved ONCE for the whole call — every
      // row below is that same person's decision.
      const decidedBy = actor(s);
      const rows = s.transactions.filter((t) => t.requestId === reqId && t.status === "pending");
      rows.forEach((stRow) => {
        stRow.status = resultStatus;
        // The row was written when the CHILD asked, so the adult on it (if any) is not the
        // one who said no. Stamp the decider over it: this row is the record of a decision
        // now, and a turned-down transfer is the one rejection a child actually sees.
        stampActor(stRow, decidedBy);
        const reverseType = stRow.type === "credit" ? "debit" : "credit";
        const delta = reverseType === "credit" ? stRow.amount : -stRow.amount;
        const p = s.profiles.find((pp) => pp.id === stRow.childId);
        if (p) {
          if (stRow.balance === "vault") p.vaultBalancePence = (p.vaultBalancePence || 0) + delta;
          else p.tokenBalance += delta;
        }
        s.transactions.unshift({
          id: uid(),
          deviceId: DEVICE_ID,
          childId: stRow.childId,
          amount: stRow.amount,
          reason: stRow.reason,
          type: reverseType,
          balance: stRow.balance,
          timestamp: nowIso(),
          reversalOf: stRow.id,
          ...(decidedBy && decidedBy.id ? { adultId: decidedBy.id } : {}),
          ...(decidedBy && decidedBy.name ? { adultName: decidedBy.name } : {}),
          ...(stRow.vaultTransfer ? { vaultTransfer: stRow.vaultTransfer === "to" ? "from" : "to", tokens: stRow.tokens, pence: stRow.pence } : {}),
        });
      });
      const r = s.requests.find((rr) => rr.id === reqId);
      if (r) { r.status = resultStatus; r.resolvedAt = nowIso(); stampActor(r, decidedBy); }
      return s;
    });
  }

  // A kid can have at most this many requests waiting on a parent at once — once hit, they
  // need a parent to clear some out before sending more, rather than the queue growing forever.
  const PENDING_REQUEST_CAP = 5;

  // Sends tokens to the Savings Vault. Unlike every other request type, this moves both
  // balances immediately (via moveTokensToVault, tagged "pending") so the child gets the
  // savings-habit payoff of seeing their balance change right away, instead of waiting on a
  // parent — the linked "vault_transfer" request still gates final confirmation, and the
  // transaction pair stays tagged "pending" until a parent approves or rejects it (see
  // RequestsTab's approve()/reject() and rejectVaultTransfer above).
  function submitVaultTransfer(childId, tokens, pence) {
    const pendingCount = state.requests.filter((r) => r.childId === childId && r.status === "pending").length;
    if (pendingCount >= PENDING_REQUEST_CAP) {
      showToast(ct("toast.requests_limit", "You've got 5 requests waiting already — ask your parent to review some first."), "bad");
      return false;
    }
    const requestId = uid();
    update((s) => {
      s.requests.unshift({ id: requestId, childId, requestType: "vault_transfer", title: `Move ${tokens} tokens to Savings Vault`, requestedAmount: tokens, goalId: null, status: "pending", timestamp: nowIso(), pence });
      return s;
    });
    moveTokensToVault(childId, tokens, pence, requestId, "pending");
    showToast(ct("toast.sent_confirm_transfer", "Sent to parent to confirm the transfer"), "info");
    track("vault_transfer_requested", { band: ageBand(state.profiles.find((p) => p.id === childId)) || undefined, value: tokens });
    return true;
  }

  // LB-381 (Alan, 27 Sep): a child sends the words off their own homework sheet to a parent.
  //
  // THE PHOTO NEVER LEAVES THE DEVICE, and this is deliberate, not an oversight to be tidied up
  // later. The reader runs in the browser, the image is never uploaded and never stored, and the
  // request carries WORDS ONLY. Attaching it would turn a homework camera into a general way for
  // a child to send pictures to a parent under a homework label — "photo sharing by the back
  // door" (Alan, 27 Sep) — which is not what this is, and not a channel worth opening for the
  // sake of a convenience. A parent who cannot make sense of the words asks for another picture.
  // scripts/check-homework.mjs asserts a submission carries no image so this cannot drift.
  // It is an ordinary request, which is the whole point — the Requests badge, the parent's
  // approve/reject and the push the worker already sends when any request is resolved all come
  // for free. Its cap is its OWN (HW_MAX_SUBMISSIONS), not the five that govern earning asks:
  // a sheet arriving is not the same kind of ask as "can I have three tokens", and neither
  // should crowd the other out.
  function submitHomeworkSheet(childId, pairs, droppedCount, listType = "spelling") {
    if (!homeworkUnlocked(state.settings)) return false; // Premium: a child's Add new homework is hidden too
    if (!canSubmitHomework(state.requests, childId)) {
      showToast(ct("toast.homework_sheets_limit", "You have sheets waiting already — ask a parent to look at those first."), "bad");
      return false;
    }
    const requestId = uid();
    update((s) => {
      s.requests.unshift({
        id: requestId, childId, requestType: HW_SUBMISSION_TYPE,
        // Alan, 28 Sep: the child says what kind of sheet it is, so the parent's review and the
        // list it becomes know whether these are spellings or words in another language.
        title: listType === "language" ? ct("homework.submit.request_title_language", "A language sheet") : ct("homework.submit.request_title", "A spelling sheet"),
        listType: listType === "language" ? "language" : "spelling",
        requestedAmount: 0, goalId: null, status: "pending", timestamp: nowIso(),
        pairs: pairs.map(({ a, b }) => ({ a, b: b || "" })), droppedCount: droppedCount || 0,
      });
      return s;
    });
    showToast(ct("toast.homework_sheet_sent", "Sent to a parent to check"), "info");
    track("homework_sheet_submitted", { band: ageBand(state.profiles.find((p) => p.id === childId)) || undefined, value: pairs.length });
    return true;
  }

  // Approving it makes the list, and lands on the LB-377 rules rather than inventing new ones:
  // on the grid if there is room, a draft if not, and `archiveFirst` naming the list the parent
  // picked to come off when both were full.
  // LB-382: one place that makes a homework list, so a sheet a child sent and a list another
  // family shared land by exactly the same rules — on the grid if there is room, a draft if not,
  // and `archiveFirst` naming whatever the parent picked to come off when both were full.
  function addHomeworkList(s, childId, meta, until, archiveFirst) {
    // Premium (Alan, 2 Oct): the write itself refuses, so a stale screen cannot get round the gate.
    if (!homeworkUnlocked(s.settings)) return null;
    const today = todayStr();
    if (!s.learningContent) s.learningContent = {};
    if (!s.learningContent[childId]) s.learningContent[childId] = { spellingWords: [], vocabBank: [] };
    const lc = s.learningContent[childId];
    if (!lc.homework) lc.homework = { lists: [] };
    if (archiveFirst) { const off = findHomeworkList(lc, archiveFirst); if (off) archiveList(off, today); }
    // mintHomeworkCode takes the codes already in use, so a new one can never collide. It was
    // handed a function here, which is why approving a sheet and adding a shared list both
    // crashed on the phone with "(e||[]).map is not a function" (Alan, staging, 28 Sep).
    const allCodes = Object.values(s.learningContent || {}).flatMap((x) => homeworkLists(x).map((l) => l.code));
    const code = mintHomeworkCode(allCodes);
    const list = newHomeworkList({
      code, type: meta.type || "spelling", name: meta.name, lang: meta.lang || "", direction: meta.direction || "en2lang",
      pairs: meta.pairs, oinksOn: meta.oinksOn !== false, finishTokens: meta.finishTokens || 0, until, createdAt: nowIso(),
      actor: actor(s), makeId: () => uid(), status: canGoLive(lc, today) ? null : "draft",
    });
    lc.homework.lists.unshift(list);
    queueSpellingAudio(s, list);
    pruneHomework(lc, today);
    return code;
  }

  // Approving a sheet the child sent, or a list another family shared: both are requests and
  // both land through addHomeworkList. `extra.childId` is the child the parent picked for a
  // shared list (which arrives with none); `extra.lang` is the language the parent named for a
  // language sheet.
  function approveHomeworkSheet(req, pairs, until, archiveFirst, extra = {}) {
    if (!req || req.status !== "pending") return;
    // Premium: adding homework (a child's photo or a shared one) is starting something new. The
    // request stays pending; declining it is never gated.
    if (!homeworkUnlocked(state.settings)) return;
    const childId = extra.childId || req.childId;
    if (!childId) return;
    const type = req.listType === "language" || req.listType === "english" ? req.listType : "spelling";
    // LB-382 receive fixes: a shared list is never given to a child who already has it (any
    // place: live, draft, shelf). Compared on the pairs actually being added, since the review
    // can edit rows. Checked here for the toast (an update() runs later, so a value found inside
    // it cannot drive one) and again inside the updater, so a replayed write can't add it either.
    const dupName = state.profiles.find((p) => p.id === childId)?.name || "";
    if (req.requestType === HW_SHARED_TYPE && childHasSameList(state.learningContent && state.learningContent[childId], type, pairs)) {
      showToast(ct("toast.shared_list_duplicate", "{name} already has this homework", { name: dupName }), "info");
      return;
    }
    update((s) => {
      const r = s.requests.find((x) => x.id === req.id);
      if (!r || r.status !== "pending") return null;
      if (r.requestType === HW_SHARED_TYPE && childHasSameList(s.learningContent && s.learningContent[childId], type, pairs)) return null;
      if (!homeworkUnlocked(s.settings)) return null;
      const code = addHomeworkList(s, childId, { type, name: String(extra.name || "").trim().slice(0, 60) || r.title || ct("homework.submit.default_name", "Homework sheet"), lang: extra.lang || r.lang || "", direction: extra.direction || r.direction || "en2lang", pairs, oinksOn: extra.oinksOn, finishTokens: extra.finishTokens }, until, archiveFirst);
      if (!code) return null; // addHomeworkList refused (Premium): nothing is written, the request stays pending
      r.childId = childId; r.status = "approved"; r.resolvedAt = nowIso(); r.listCode = code; stampActor(r, actor(s));
      // The Oinks the child earned trying this list in the browser before the family signed up
      // (see where the request is filed). Paid once, here, to the child the list is given to,
      // exactly as onEarnOinks pays a go — and not at all if the parent has Oinks switched off
      // for that child. Outside the money ledger, like every Oink (Law 1).
      if (r.requestType === HW_SHARED_TYPE && r.guestOinks > 0 && !r.guestOinksPaid) {
        const lc = s.learningContent && s.learningContent[childId];
        const c = s.profiles.find((x) => x.id === childId);
        if (c && !(lc && lc.oinksEnabled === false)) {
          ensureCosmetics(c);
          c.oinks += r.guestOinks;
          recordOinkEarn(c, r.guestOinks);
          r.guestOinksPaid = true;
        }
      }
      return s;
    });
    const addedName = state.profiles.find((p) => p.id === childId)?.name || "";
    const oinksOff = state.learningContent && state.learningContent[childId] && state.learningContent[childId].oinksEnabled === false;
    showToast(req.requestType === HW_SHARED_TYPE && req.guestOinks > 0 && !oinksOff
      ? ct("toast.homework_sheet_added_oinks", "Added to {name}'s homework, with the {n} Oinks from the practice", { name: addedName, n: req.guestOinks })
      : ct("toast.homework_sheet_added", "Added to {name}'s homework", { name: addedName }), "good");
  }

  // LB-382: a list another family shared, arriving as ?practice=HW-XXXXXX. The code is taken
  // out of the address bar straight away so a refresh, a screenshot or a shared browser history
  // does not re-open somebody else's list.
  //
  // Second cut (Alan, staging, 28 Sep). The first cut held the list in memory and painted it
  // over the Home tab: no badge, no request, nothing to review, and gone if the app closed. It
  // now becomes a pending REQUEST, exactly as a sheet the child sent is one — the Requests badge
  // counts it, it waits there until a parent decides, and Check the words opens the same review.
  // In Parent Mode the tab switches to Requests so the parent who just tapped the link lands on
  // it; in Kid Mode nothing is said and it waits for a parent.
  // Alan, 28 Sep: a parent who has just tapped the link gets the review in an overlay straight
  // away, on top of whatever tab they were on. Closing it leaves the request in the tray.
  const [sharedOverlay, setSharedOverlay] = useState(null); // a request id
  // LB-315 (29 Sep, supersedes R-D's parent-tab-only history): the back gesture on every
  // surface goes through src/lib/backstack.js — one popstate listener. This block owns the
  // root, the tabs of both modes (decision A: back steps tabs; at the root back is the
  // platform's own — decision B, 30 Sep, after Safari drew a stale snapshot for the "floor")
  // and the unsaved-changes guard on a popped parent tab; every overlay registers itself with
  // useBackLayer where it is rendered. The wizard is out of scope (Alan, 29 Sep).
  const parentTabRef = useRef(parentTab); parentTabRef.current = parentTab;
  const childTabRef = useRef(childTab); childTabRef.current = childTab;
  const modeRef = useRef(mode); modeRef.current = mode;
  const tabFromPopRef = useRef(false);
  useEffect(() => installBackStack(() => modeRef.current), []);
  // A mode switch starts a new generation: the module walks back over the old mode's entries
  // and lays the new root there, so nothing of Parent Mode ever sits beneath Kid Mode.
  useEffect(() => { resetRoot(mode, mode === "parent" ? parentTabRef.current : childTabRef.current); }, [mode]);
  useEffect(() => registerTabs("parent", {
    get: () => parentTabRef.current,
    guard: () => {
      const guard = unsavedGuardRef.current;
      if (guard && guard.hasUnsavedChanges()) {
        const target = (window.history.state || {}).lbTab;
        setPendingNav(() => () => { tabFromPopRef.current = false; setParentTab(target); });
        return false;
      }
      return true;
    },
    set: (tab) => { tabFromPopRef.current = true; setParentTab(tab); },
  }), []);
  useEffect(() => registerTabs("child", { get: () => childTabRef.current, set: (tab) => { tabFromPopRef.current = true; setChildTab(tab); } }), []);
  useEffect(() => {
    if (mode !== "parent" || wizardActive) return;
    if (tabFromPopRef.current) { tabFromPopRef.current = false; return; }
    tabChanged(parentTab);
  }, [parentTab, mode, wizardActive]);
  useEffect(() => {
    if (mode !== "child") return;
    if (tabFromPopRef.current) { tabFromPopRef.current = false; return; }
    tabChanged(childTab);
  }, [childTab, mode]);
  // App-level layers: the PIN pad over Kid Mode, Help & support, the shared-list overlay.
  useBackLayer(!!pinPrompt, () => { setPinPrompt(false); setPinError(""); });
  useBackLayer(!!helpOpen, () => setHelpOpen(null));
  useBackLayer(!!sharedOverlay, () => setSharedOverlay(null));
  // ---- Entering Parent Mode, and the waiting-requests overlay (Alan, 2 Oct) ----------------
  //
  // ENTERING FROM KID MODE OPENS HOME. Every way in from Kid Mode (the PIN pad's submitPin,
  // device set-up's finish, the no-PIN-yet header button, and the two deep links once their PIN
  // is proved) changes `mode` from child to parent, so this one effect decides where they land
  // instead of each site deciding for itself. A deep link keeps its destination: the requests
  // push lands on Requests, the set-up link in the wizard. A boot straight into Parent Mode (a
  // reload while there) is not a child-to-parent change and keeps the stored tab. ParentView
  // unmounts in Kid Mode, so the Learn & Earn hub and a child's page left open last time are
  // gone as well; entering is the top of Home.
  //
  // THE OVERLAY lists what is waiting (RequestsOverlay). "The primary (not only) purpose of
  // this overlay is to alert the user to homework requests waiting for review." It shows:
  //   - on entering from Kid Mode, and on a new family's first arrival after set-up, when a
  //     pending request is one this DEVICE has not shown in it before (REQUESTS_OVERLAY_SHOWN);
  //   - when a homework request this device has not shown ARRIVES while the parent is on Home
  //     with nothing else open (Send it to my app is collected a moment after the app opens,
  //     so a quick unlock is on Home before it lands). An ordinary request never pops it up;
  //   - never on a reload, a deep link, or over the wizard, set-up complete, device set-up, the
  //     PIN pad or a shared homework's review. Every adult sees Requests today, so every adult
  //     can see this.
  // Every way of closing it marks what is waiting as shown on this device. It replaced
  // CarriedHomeworkPrompt (1 Oct): the homework a family signed up with is a row in it now.
  const [requestsOverlayOpen, setRequestsOverlayOpen] = useState(false);
  const [requestsFocus, setRequestsFocus] = useState(null); // { id, n }: RequestsTab's "show this request"
  const overlayArmedRef = useRef(false);
  const homeBusyRef = useRef(false); // ParentView reports the Learn & Earn hub open over Home
  const pendingRequests = (state.requests || []).filter((r) => r.status === "pending");
  // The preview household (?preview=app, the Playwright suite's fixture) has requests waiting
  // from the start, so the overlay would sit over every spec that enters Parent Mode. It stays
  // off there unless ?ro=1 asks for it.
  const overlayPreviewOff = code === "PREVIEW" && new URLSearchParams(window.location.search).get("ro") !== "1";
  const overlayBlocked = overlayPreviewOff || mode !== "parent" || wizardActive || showSetupComplete || deviceSetupStep !== null || pinPrompt || !!sharedOverlay;
  const shownKey = REQUESTS_OVERLAY_SHOWN_KEY_PREFIX + code;
  function readOverlayShown() {
    try { const v = JSON.parse(localStorage.getItem(shownKey) || "[]"); return Array.isArray(v) ? v : []; } catch { return []; }
  }
  function markOverlayShown(ids) {
    // Kept to ids still pending, so the record never outgrows what is waiting.
    try {
      const live = new Set(pendingRequests.map((r) => r.id));
      localStorage.setItem(shownKey, JSON.stringify([...new Set([...readOverlayShown(), ...ids])].filter((id) => live.has(id))));
    } catch {}
  }
  const prevModeRef = useRef(mode);
  useEffect(() => {
    const prev = prevModeRef.current;
    prevModeRef.current = mode;
    if (mode !== "parent") { overlayArmedRef.current = false; setRequestsOverlayOpen(false); return; }
    if (prev !== "child") return;
    if (deepLinkToRequestsRef.current) { deepLinkToRequestsRef.current = false; setParentTab("requests"); return; }
    if (deepLinkToSetupRef.current) { deepLinkToSetupRef.current = false; return; }
    setParentTab("home");
    overlayArmedRef.current = true;
  }, [mode]);
  // Armed by an entry; shown once the way is clear and the parent is on Home.
  useEffect(() => {
    if (!overlayArmedRef.current || overlayBlocked || requestsOverlayOpen || parentTab !== "home") return;
    overlayArmedRef.current = false;
    const shown = new Set(readOverlayShown());
    if (pendingRequests.some((r) => !shown.has(r.id))) setRequestsOverlayOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayBlocked, requestsOverlayOpen, parentTab, mode]);
  // A homework request arriving while the parent is on Home with nothing open.
  const knownPendingRef = useRef(null);
  useEffect(() => {
    const known = knownPendingRef.current;
    knownPendingRef.current = new Set(pendingRequests.map((r) => r.id));
    if (!known) return;
    const arrived = pendingRequests.filter((r) => !known.has(r.id) && isHomeworkRequest(r));
    if (!arrived.length || overlayBlocked || requestsOverlayOpen || parentTab !== "home" || homeBusyRef.current) return;
    if (typeof document !== "undefined" && document.querySelector('[role="dialog"]')) return;
    const shown = new Set(readOverlayShown());
    if (arrived.some((r) => !shown.has(r.id))) setRequestsOverlayOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.requests]);
  function closeRequestsOverlay() {
    markOverlayShown(pendingRequests.map((r) => r.id));
    setRequestsOverlayOpen(false);
  }
  useBackLayer(requestsOverlayOpen && !overlayBlocked && pendingRequests.length > 0, closeRequestsOverlay);
  // A row: a homework opens its review (a shared one here, a child's sheet in the Requests tab);
  // anything else opens the Requests tab at that request. Nothing is approved or refused.
  function openFromRequestsOverlay(r) {
    closeRequestsOverlay();
    if (r.requestType === HW_SHARED_TYPE) { setSharedOverlay(r.id); return; }
    setParentTab("requests");
    setRequestsFocus({ id: r.id, n: Date.now() });
  }
  function seeAllFromRequestsOverlay() {
    closeRequestsOverlay();
    setParentTab("requests");
  }
  useEffect(() => {
    // LB-382 (1 Oct): or the list a NEW family arrived with, carried across sign-up in this
    // browser (Root sets it when the list lets them past the gate). That one is filed quietly:
    // they are in the middle of the set-up wizard with no child yet, so there is nothing to
    // review it against and an overlay would land on top of the wizard. It waits in Requests.
    const fromUrl = sharedCodeFromUrl();
    const carried = fromUrl || code === "PREVIEW" ? null : takePendingSharedCode();
    const listCode = fromUrl || carried;
    if (!listCode) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("practice");
      window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
    } catch { /* an address bar we cannot rewrite is not a reason to drop the list */ }
    let live = true;
    fetchSharedList(listCode).then((list) => {
      if (!live) return;
      if (!list) { showToast(ct("toast.shared_list_gone", "That homework is no longer being shared."), "info"); return; }
      const requestId = uid();
      let openId = requestId;
      const guest = carried ? readGuestResult(list.code) : null;
      const guestRight = guest ? Math.max(0, Math.floor(guest.right)) : 0;
      update((s) => {
        const already = findPendingShared(s.requests, list.code); // the same link, opened twice
        if (already) { openId = already.id; return null; }
        const shared = sharedListRequest(list, { id: requestId, now: nowIso() });
        // Alan, 1 Oct: "claim the Oinks you just earned". A family that tried the list in this
        // browser before signing up earned one Oink per right answer, as the app pays for
        // practising. They ride on the request and are paid to whichever child the parent
        // gives the list to (approveHomeworkSheet). ONLY on the carried path: the result is
        // this browser's own, and a tapped link or the worker's inbox never brings one. Capped
        // at the list's length, since the stored result is only as trustworthy as a browser.
        if (carried && guestRight > 0) shared.guestOinks = Math.min(guestRight, shared.pairs.length);
        // `carried` marks the homework a NEW family arrived with: its row in the waiting-requests
        // overlay says "Tried before you signed up" (2 Oct; this was CarriedHomeworkPrompt's job
        // on 1 Oct). A tapped link opens its own review there and then, and the inbox comes
        // with a push.
        if (carried) shared.carried = true;
        s.requests.unshift(shared);
        return s;
      });
      if (carried) clearGuestResult(list.code);
      if (modeRef.current === "parent" && !carried) {
        setParentTab("requests");
        setSharedOverlay(openId);
      }
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LB-382 (Alan, 1 Oct): "Send it to my app". A list sent from a browser to this household's
  // email waits in the household's inbox on the worker; the push that announces it opens
  // /?goto=requests. So as the app opens — and whenever it comes back to the front, which is
  // what a tapped push does to an app that was already running — the inbox is collected and
  // each list becomes the SAME pending request a tapped link makes above. Nothing else is new:
  // the Requests badge, "Check the words", the child picker, the rewards and the review are
  // all the existing ones.
  //
  // It runs in Kid Mode too, exactly as a tapped link does: the request is filed and nothing
  // is said until a parent opens Requests. A list that is no longer being shared is cleared
  // from the inbox without a word — there is nothing for the parent to do about it.
  const inboxBusyRef = useRef(false);
  async function collectSharedInbox() {
    if (inboxBusyRef.current || code === "PREVIEW") return;
    inboxBusyRef.current = true;
    try {
      const listCodes = await fetchHomeworkInbox(code);
      if (!listCodes.length) return;
      for (const listCode of listCodes) {
        const list = await fetchSharedList(listCode);
        if (!list) continue;
        update((s) => {
          if (findPendingShared(s.requests, list.code)) return null; // already waiting
          s.requests.unshift(sharedListRequest(list, { id: uid(), now: nowIso() }));
          return s;
        });
      }
      await clearHomeworkInbox(code, listCodes);
    } finally { inboxBusyRef.current = false; }
  }
  useEffect(() => {
    collectSharedInbox();
    const onVisible = () => { if (document.visibilityState === "visible") collectSharedInbox(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function rejectHomeworkSheet(req, reason) {
    if (!req || req.status !== "pending") return;
    update((s) => {
      const r = s.requests.find((x) => x.id === req.id);
      if (!r || r.status !== "pending") return null;
      r.status = "rejected"; r.resolvedAt = nowIso(); r.parentNote = String(reason || "").slice(0, 240); stampActor(r, actor(s));
      return s;
    });
    if (req.requestType === HW_SHARED_TYPE) showToast(ct("toast.shared_list_declined", "Not added"), "info");
    else showToast(ct("toast.homework_sheet_returned", "Sent back for another picture"), "info");
  }

  // Spending-cap gating only applies to "earn" requests (the ad-hoc "Did something else" ask
  // and earning-activity pills both submit as this type) — goal/experience redemptions and
  // vault transfers aren't new earnings, so they're never subject to it. See
  // capStatusForNewEarning for the shared three-tier rule.
  function submitRequest(childId, requestType, title, amount, goalId, extra) {
    const pendingCount = state.requests.filter((r) => r.childId === childId && r.status === "pending").length;
    if (pendingCount >= PENDING_REQUEST_CAP) {
      showToast(ct("toast.requests_limit", "You've got 5 requests waiting already — ask your parent to review some first."), "bad");
      return false;
    }
    // LB-17: one open ask-to-buy per child (Alan, 28 Sep) — the card offers no way to send a
    // second while one waits, and this guards the state itself.
    if (requestType === "ask_to_buy" && state.requests.some((r) => r.childId === childId && r.requestType === "ask_to_buy" && r.status === "pending")) {
      showToast(ct("toast.ask_to_buy_open", "You already have an ask waiting on a grown-up."), "bad");
      return false;
    }
    let capExtra = {};
    if (requestType === "earn") {
      const child = state.profiles.find((p) => p.id === childId);
      // LB-248: on the child's double token day an earning activity asks for double. The
      // request carries baseAmount + doubleDay so the parent's card shows the base struck
      // through, and the cap check below runs on the DOUBLED amount — the cap stands.
      if (isDoubleTokenDay(childId, todayStr(), state.settings)) {
        capExtra = { baseAmount: amount, doubleDay: true };
        amount = amount * 2;
      }
      const capStatus = capStatusForNewEarning(child, state.transactions, state.requests, state.settings?.exchangeRate, amount);
      if (capStatus.blocked) {
        const capGBP = (child.spendingCap.weeklyGBP || 0).toFixed(2);
        showToast(ct("toast.earning_cap_reached", "You've reached your {symbol}{cap} earning limit for the week — chat to your parent if you'd like to do more.", { symbol: CURRENCY_SYMBOL, cap: capGBP }), "bad");
        return false;
      }
      if (capStatus.breaches) capExtra = { ...capExtra, breachesCap: true };
    }
    update((s) => {
      s.requests.unshift({ id: uid(), childId, requestType, title, requestedAmount: amount, goalId: goalId || null, status: "pending", timestamp: nowIso(), ...(extra || {}), ...capExtra });
      return s;
    });
    showToast(requestType === "vault_transfer" ? ct("toast.sent_confirm_transfer", "Sent to parent to confirm the transfer") : requestType === "ask_to_buy" ? ct("toast.sent_ask_to_buy", "Sent to a grown-up to decide") : ct("toast.sent_for_approval", "Sent to parent for approval"), "info");
    return true;
  }

  // Lets a child withdraw their own not-yet-resolved request (e.g. a bank transfer they
  // changed their mind about). Kept distinct from a parent's reject so history stays accurate.
  function cancelRequest(reqId) {
    const req = state.requests.find((x) => x.id === reqId);
    if (!req || req.status !== "pending") return;
    if (req.requestType === "vault_transfer") {
      // Already moved for real (see submitVaultTransfer) — cancelling has to put the
      // balances back, not just flip a status label.
      rejectVaultTransfer(reqId, "cancelled");
    } else {
      update((s) => {
        const r = s.requests.find((x) => x.id === reqId);
        if (r && r.status === "pending") { r.status = "cancelled"; r.resolvedAt = nowIso(); }
        return s;
      });
    }
    showToast(ct("toast.request_cancelled", "Request cancelled"), "info");
  }

  // Toggles one task's completion for one day. If that happens to complete every applicable
  // task for the day AND pushes this week's complete-day count to the child's threshold, and
  // no reward has already been raised for this week, raises the weekly reward — credited on
  // the spot in "auto" mode (household switch in Daily habits, LB-13), or a habit_reward
  // request for a parent to tap in "manual" mode. See raiseHabitReward in lib/utils.js.
  function toggleHabit(childId, taskId, dateStr, image) {
    update((s) => {
      // Once a parent has approved this week's streak reward, the week is locked — the tasks
      // that earned it can't be unticked (or re-ticked) after the fact. This mirrors the
      // disabled checkboxes in StreakCard, but enforced here too as a safety net in case a
      // stale UI somehow still lets a tap through.
      const weekStart = weekStartOf(dateStr);
      const weekLocked = s.requests.some((r) => r.requestType === "habit_reward" && r.childId === childId && r.weekStart === weekStart && r.status === "approved");
      if (weekLocked) return s;

      const idx = s.habitLog.findIndex((l) => l.childId === childId && l.taskId === taskId && l.date === dateStr);
      if (idx >= 0) s.habitLog.splice(idx, 1);
      else s.habitLog.push({ id: uid(), childId, taskId, date: dateStr, ...(image ? { image } : {}) });

      const child = s.profiles.find((p) => p.id === childId);
      if (child && isHabitDayComplete(s.habitLog, s.habits, childId, dateStr)) {
        const { weekStart, completeDays } = habitWeekProgress(s.habitLog, s.habits, childId, dateStr);
        const threshold = child.habitThreshold || 7;
        const reward = child.habitReward || 20;
        // LB-13: one shared raise — auto mode credits on the spot (cap permitting), manual
        // raises the request. Idempotent per week inside raiseHabitReward.
        if (completeDays >= threshold) raiseHabitReward(s, child, weekStart, completeDays, threshold, reward, DEVICE_ID);
      }
      return s;
    });
    // Habit streak badges (Week/Month of Fire) can only move on habit events — evaluate
    // right after the log changes. Cheap: stored badges short-circuit everything earned.
    awardNewBadges(childId);
    track("habit_ticked", { band: ageBand(state.profiles.find((p) => p.id === childId)) || undefined });
  }

  // ---- Trophy Cabinet (lib/badges.js) ----
  // Evaluate any not-yet-earned badges for a child and, if something unlocked, toast it and
  // fire the pig celebration in ChildView. The state write itself (badge stored, +10 OP
  // applied) happens correctly inside update()'s functional updater regardless of anything
  // below — but update() is React's deferred setState(prev => ...) form, so the updater does
  // NOT run synchronously when update() is called. Reading its result on the very next line
  // would read a stale closure value. Instead: the updater stashes the newly-earned ids on a
  // ref, and a same-render-cycle effect (no dependency array — runs after every commit, so it
  // picks the ref up on the render where the state change actually lands) flushes it into a
  // toast + celebration bump exactly once, then clears the ref.
  const [badgeCelebrateKey, setBadgeCelebrateKey] = useState(0);
  // Perfect Week (Aug 2026 characters/economy task) — same bump-a-counter celebration
  // trigger as badgeCelebrateKey just above; ChildView watches it the same way.
  const [perfectWeekCelebrateKey, setPerfectWeekCelebrateKey] = useState(0);
  const pendingBadgeToastRef = useRef(null);
  function awardNewBadges(childId) {
    // W1.7 follow-up (production-gate red, Aug 2026). Two rules, both load-bearing:
    // 1. The award decision runs INSIDE the updater, against the fresh `s` React hands us —
    //    two call sites (toggleHabit, onSessionFinished) invoke this immediately after
    //    another update() in the same tick, so any probe against the render closure's
    //    `state` would be one commit stale and could skip a just-earned badge (e.g. the
    //    habit tick that completes Week of Fire). Returning `null` (see Root.update) makes
    //    the no-award case a true no-op: no revision bump, no KV write — the old shape
    //    burned a write on every kid-mode entry for nothing, against the repo's standing
    //    write-minimisation rule.
    // 2. `auto`: a badge award is machine-initiated, never a user edit — it must not stamp
    //    lastLocalEditRef, which offline dirty detection reads as "the user was active
    //    here". Before this, the boot-time sweep marked an UNTOUCHED offline device dirty,
    //    and its reconnect PUSHED the stale cache over other devices' newer work. Awards
    //    are idempotent (stored badges never re-fire), so re-detection after a reconnect
    //    pull is safe by design.
    update((s) => {
      const newly = checkAndAwardBadges(s, childId);
      if (newly.length === 0) return null;
      pendingBadgeToastRef.current = newly;
      return s;
    }, { auto: true });
  }
  useEffect(() => {
    if (!pendingBadgeToastRef.current) return;
    const newly = pendingBadgeToastRef.current;
    pendingBadgeToastRef.current = null;
    const names = newly.map((id) => { const b = findBadge(id); return b ? b.label : id; }).join(", ");
    showToast(ct("toast.new_badges", "New badge{plural}: {names} (+{bonus} Oinks)", { plural: newly.length > 1 ? "s" : "", names, bonus: BADGE_OINK_BONUS * newly.length }), "good");
    setBadgeCelebrateKey((k) => k + 1);
  });

  // Once per kid-mode entry (and on child switch), sweep for badges earned by parent-side
  // events since the child's last visit — an approved goal redemption or vault transfer
  // unlocks Goal Getter / Super Saver here, so the celebration lands on the child's next
  // open, which is the better moment for it anyway. Idempotent: stored badges never re-fire.
  useEffect(() => {
    if (mode === "child" && activeChild) awardNewBadges(activeChild.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, activeChild?.id]);

  // How many requests are sitting waiting on a parent right now. Already drove the badge on
  // the Requests tab (visible only once you're inside Parent Mode, which is the one place you
  // were always going to look anyway) — now also drives the badge on the Parent Mode button
  // itself and the OS-level app icon badge, so the signal reaches someone who hasn't opened
  // Parent Mode and someone who hasn't opened the app at all.
  const pendingRequestCount = useMemo(
    () => state.requests.filter((r) => r.status === "pending").length,
    [state.requests]
  );

  // The app-icon badge is the one notification surface that needs no permission prompt on
  // Android and is covered by the notification permission on iOS 16.4+ — so on a device where
  // a parent declined push, this is still a live channel. Silently unavailable elsewhere,
  // which is fine: it's additive to the in-app badges, never the only signal.
  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;
    try {
      if (pendingRequestCount > 0) navigator.setAppBadge(pendingRequestCount);
      else navigator.clearAppBadge?.();
    } catch {
      // Some browsers expose the method but reject the call outside an installed context.
    }
  }, [pendingRequestCount]);

  // Repair a rotated/dropped push endpoint on every app open — see resyncPushSubscription.
  useEffect(() => { resyncPushSubscription(code); }, [code]);

  // LB-340: the pad hands back WHOSE PIN this was — its own adult, or the one the parent
  // picked through "Log in as". null means "the Owner", which is what the worker reads an
  // absent adultId as.
  async function submitPin(digits, adultId) {
    setPinChecking(true);
    try {
      const res = await apiVerifyPin(code, digits, adultId);
      const ok = res.valid;
      if (ok) {
        setMode("parent"); setPinPrompt(false); setPinError("");
        setVerifiedPin(digits);
        const proved = res.adultId || adultId || null; // see the other unlock site
        setActiveAdultId(proved);
        setSessionAdultId(code, proved); // LB-343
        markParentUnlocked(code); // LB-342: starts the grace window for this tab
        if (proved) setDeviceOwnerAdultId(code, proved);
        ensureProvenDevice(code, digits, adultId); // LB-178: the PIN just typed is this device's proof
        if (deepLinkToRequestsRef.current) setParentTab("requests");
      } else {
        setPinError("Incorrect PIN — try again");
      }
    } catch (err) {
      // LB-177: a lockout carries its own wait-time message from the worker.
      setPinError(err?.rateLimited ? err.message : "Couldn't check the PIN — check your connection and try again");
    } finally {
      setPinChecking(false);
    }
  }

  // Dev-only UI icon sprite review surface (docs/ui-icons.md) — reachable at ?icons=1 with
  // no link from anywhere in the app. Renders instead of the normal app so the sprite can be
  // reviewed through the real Worker + service worker (not just locally) on a phone.
  if (new URLSearchParams(window.location.search).get("icons") === "1") return <IconGallery />;

  // Pull-to-refresh used to call window.location.reload(). That is what was throwing a
  // parent back to Kid Mode, and a child back to the first profile: a reload restarts
  // App, and `mode` and `activeChildId` are plain useState. The diagnostic added earlier
  // confirmed it — reason came back `ptr`.
  //
  // The gesture now does what a user actually means by it: re-fetch household state. That
  // is Root's own foreground refetch, reached through onSoftRefresh — the same path used
  // when the app returns to the foreground, so it inherits the offline push-first and
  // recent-local-edit guards rather than reimplementing them. Nothing unmounts, so mode,
  // active child, open tab and scroll position all survive.
  //
  // A hard reload had one legitimate secondary purpose: picking up a new deploy on a
  // long-lived session. That is kept by nudging the service worker to check for an update
  // — if a genuinely new version exists it installs and controllerchange reloads once, as
  // it already does. So a reload still happens when there IS something new, and no longer
  // happens just because someone scrolled up.
  //
  // The `ptr` stamp stays for now: it costs nothing and, if a reload ever DOES follow this
  // gesture, the next boot reports which reloader really fired.
  const softRefresh = () => {
    try { sessionStorage.setItem("lb-reload-reason", "ptr"); } catch {}
    if (onSoftRefresh) onSoftRefresh();
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistration().then((reg) => reg && reg.update()).catch(() => {});
      }
    } catch {}
  };

  return (
    <PullToRefresh onRefresh={softRefresh}>
    <div className="max-w-lg mx-auto p-3 sm:p-5 lb-frame">
      <SafeAreaSpacer />
      {/* LB-338 (C2). First in the tree so it covers whatever is behind it, and rendered on
          its own condition rather than a flag — see OwnerNameSheet.jsx for why it cannot be
          dismissed and why saving is what ends it. */}
      {needsOwnerName && (
        <OwnerNameSheet
          maxLength={OWNER_NAME_MAX}
          onSave={async (n) => { update((st) => { st.ownerName = n; const o = ownerOf(st); if (o) o.name = n; return st; }); }}
        />
      )}
      {/* The "something went wrong — tell us?" strip (docs/HELP_SCOPE.md §4). Sits above
          the offline banner because it's the more urgent of the two: offline is a state
          the user can wait out, an error is one they can't. Rose rather than amber —
          amber is the offline/attention tone and these must not read as the same thing.
          Shown at most once per load (see errorStripShownRef) and dismissible, so an
          error loop can't pin it to the screen. */}
      {errorStrip && (
        <div data-testid="error-report-strip" className="bg-rose-50 border border-rose-200 rounded-2xl px-3 py-2 mb-2 flex items-center gap-2">
          <span className="flex-1 text-rose-700 text-xs font-semibold">{ct("report.error_prompt", "Something went wrong. Tell us what you were doing?")}</span>
          <button
            data-testid="error-report-cta"
            onClick={() => { openHelp({ view: "report", errorHint: errorStrip }); setErrorStrip(null); }}
            className="flex-shrink-0 bg-rose-600 text-white text-xs font-bold rounded-lg px-2.5 py-1.5"
          >
            {ct("report.error_cta", "Report it")}
          </button>
          <button aria-label="Dismiss" onClick={() => setErrorStrip(null)} className="flex-shrink-0 text-rose-400 text-lg leading-none px-1">×</button>
        </div>
      )}
      {/* W1.7 — offline banner: rendered in BOTH modes, above everything, whenever Root is
          serving the cached household or a save couldn't land. Amber per the icon-palette
          rule (attention only, never decorative) — this is exactly an attention state. */}
      {offline && (
        <div data-testid="offline-banner" className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold rounded-2xl px-3 py-2 text-center mb-2">
          {ct("offline.banner", "You're offline — showing your last saved info. Changes will be saved when you're back online.")}
        </div>
      )}
      {pinPrompt && <PinPad state={state} onSubmit={submitPin} onCancel={() => {
        setPinPrompt(false);
        // A pad that was ARMED AT BOOT (parent-role device, or an unfinished setup) sits over
        // a Parent Mode that nobody has proved a PIN for yet. Dismissing it must not leave
        // that surface on screen: fall back to Kid Mode, exactly where the Parent Mode button
        // path would have left you. No-op when the pad was raised from Kid Mode.
        if (mode === "parent" && !verifiedPin && state.settings.pinConfigured) setMode("child");
      }} error={pinError} code={code} />}
      {helpOpen && (
        <HelpModal
          onClose={() => setHelpOpen(null)}
          code={code}
          showToast={showToast}
          mode={mode}
          ownerEmail={state.ownerEmail || ""}
          openTo={helpOpen.view}
          errorHint={helpOpen.errorHint}
        />
      )}
      {pendingNav && (
        // Same Save/Discard/Keep-editing shape as ProfilesTab's own child-to-child version
        // (see that component's comment for why one exists there too) — this one fires for
        // navigation ProfilesTab has no visibility into at all (leaving its tab, leaving
        // Parent Mode entirely), so it lives here instead. Deliberately generic copy: this
        // level only knows "there's an unsaved child edit somewhere," not which child or
        // what changed — ProfilesTab owns that detail behind its imperative handle.
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-5 max-w-xs w-full shadow-2xl text-center">
            <div className="font-bold text-slate-800 mb-2">{ct("unsaved.title", "You have unsaved changes")}</div>
            <p className="text-xs text-slate-500 mb-4">{ct("unsaved.body_profile", "You've made changes to a child's profile that haven't been saved yet.")}</p>
            <button
              className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5 mb-2"
              onClick={() => {
                // trySave can fail its own validation (e.g. a blank age) — its toast
                // already explains why, so on failure just stay put: don't navigate, and
                // don't lose the pending action, since the parent may retry after fixing it.
                const ok = unsavedGuardRef.current?.trySave();
                if (ok) { pendingNav(); setPendingNav(null); }
              }}
            >{ct("unsaved.save_and_continue", "Save and continue")}</button>
            <button
              className="w-full bg-rose-50 text-rose-600 font-bold text-sm rounded-xl py-2.5 mb-2"
              onClick={() => {
                unsavedGuardRef.current?.discard();
                pendingNav();
                setPendingNav(null);
              }}
            >{ct("unsaved.discard_changes", "Discard changes")}</button>
            <button className="w-full text-slate-400 font-semibold text-xs py-1.5" onClick={() => setPendingNav(null)}>{ct("unsaved.keep_editing", "Keep editing")}</button>
          </div>
        </div>
      )}
      {showSetupComplete && (
        <SetupCompleteOverlay
          code={code}
          state={state}
          update={update}
          verifiedPin={verifiedPin}
          firstChild={visibleProfiles[0] || profiles[0] || null}
          onClose={() => setShowSetupComplete(false)}
          onShowKid={() => {
            // The overlay's primary now lands in Kid Mode on that child rather than on an
            // empty Parent Home (Alan, 30 Aug 2026). setActiveChildId matters on a
            // multi-child household: without it the switch honours whatever the wizard left
            // active, which after walking every child is the LAST one, not the one the
            // button just named.
            const target = visibleProfiles[0] || profiles[0];
            if (target) setActiveChildId(target.id);
            setShowSetupComplete(false);
            // The window ends here too. The wizard's PIN step opens it (markParentUnlocked in
            // the device-setup unlock above), and this is the third way out of Parent Mode —
            // the header button and the FAB already close it. Without this, a parent who
            // finished setup and tapped straight into Kid Mode had left Parent Mode as far as
            // they could tell, and a reload inside the next five minutes walked back in
            // without the pad (Alan, 20 Sep).
            clearParentUnlocked(code);
            setMode("child");
            window.scrollTo({ top: 0, behavior: "auto" });
          }}
        />
      )}
      {deviceSetupStep === "whose" && (
        <DeviceWhoseScreen
          profiles={profiles}
          adults={state.adults || []}
          onChoose={(role) => { setPendingDeviceRole(role); setDeviceSetupStep("confirm"); }}
          onIndividual={() => setDeviceSetupStep("kids")}
          onSkip={() => finishDeviceSetup(false)}
        />
      )}
      {deviceSetupStep === "confirm" && pendingDeviceRole && (
        <DeviceRoleConfirmScreen
          profiles={profiles}
          adults={state.adults || []}
          role={pendingDeviceRole}
          onConfirm={confirmDeviceRole}
          onBack={() => { setPendingDeviceRole(null); setDeviceSetupStep("whose"); }}
        />
      )}
      {deviceSetupStep === "pin" && (
        <PinPad state={state} onSubmit={submitDeviceSetupPin} onCancel={() => { setDeviceSetupPinError(""); setDeviceSetupStep(pendingDeviceRole ? "confirm" : "whose"); }} error={deviceSetupPinError} code={code} />
      )}
      {deviceSetupStep === "kids" && (
        <DeviceKidsToggleScreen
          profiles={profiles}
          hiddenIds={hiddenIds}
          mutedIds={mutedIds}
          onToggleVisibility={toggleChildVisibility}
          onToggleMute={toggleChildMute}
          onFinish={() => finishDeviceSetup(true)}
        />
      )}
      {newChildPrompt && deviceSetupStep === null && (
        <NewChildVisibilityPrompt
          child={newChildPrompt}
          roleLabel={deviceRoleLabel(deviceRole)}
          onKeep={() => resolveNewChild(false)}
          onShow={() => resolveNewChild(true)}
        />
      )}
      {/* Asked on entry to Parent Mode, but never on top of the setup wizard, the device-setup
          flow, the PIN pad, or the "you're all set up" overlay — each of those is already a
          full-screen moment, and stacking a second modal on one is how a prompt gets dismissed
          reflexively rather than read. */}
      {mode === "parent" && !wizardActive && !pinPrompt && !showSetupComplete && deviceSetupStep === null && !newChildPrompt && (
        <ParentNotificationPrompt code={code} />
      )}

      {/* Hairline under the header (Alan, Sep 2026): the only rule between header and body —
          the canvas continues through both, so this is the separator. */}
      {/* LB-223 (8 Sep 2026, decision 39): one 56px row. Brand left (head + wordmark in BOTH
          modes, per the same-day amendment on AppHeaderBrand's own comment), then Help, the
          icon-only Install (phones), the mode toggle, and the full Install (sm+). Both mode
          buttons are the tinted secondary (bg-pill), not a filled primary, so the page keeps
          its one navy primary (DESIGN_GUIDANCE §2 principle 5). The two-row phone wrap this
          replaced existed only because the old lockup was 72px tall. */}
      {/* 18 Sep (Alan): Kid Mode only, mb-4 -> mb-2. With the name row's wrapper losing its
          pt-1 below, that is 8px from this rule to the top of the character, down from 20px.
          Every other mode keeps mb-4. */}
      <header className={`${mode === "child" ? "mb-2" : "mb-4"} pb-3 border-b border-slate-200 flex items-center justify-between gap-2`}>
        {/* Device pass (Alan, 8 Sep): the head stays in Kid Mode after all (decision 39,
            amended) — the wordmark alone read as unbranded. The phone-width Install icon
            moves to the child row below so the row still fits at 390px. */}
        {/* LB-319 (18 Sep, docs/LB331_LB319_SPEC.md): below 385px, Kid Mode only, the wordmark
            drops out — the head is the half worth protecting per the amendment above. Parent
            Mode keeps both at every width; narrowInKidMode is only ever true here. */}
        <AppHeaderBrand variant="mark" narrowInKidMode={mode === "child"} className="flex-shrink-0" />
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => openHelp()} aria-label="Help" title="Help" className="rounded-lg px-3 py-2 text-sm font-semibold bg-pill text-navy whitespace-nowrap flex-shrink-0"><Icon id="ic-help" size={18} className="inline-block align-[-4px]" style={{ color: "currentColor" }} /></button>
          {mode !== "child" && <InstallButton code={code} variant="icon" className="sm:hidden" />}
          {/* R-D (Aug 2026, punch-list 2): Settings lives beside the Parent Mode page heading
              inside ParentView, not up here. */}
          {mode === "child" ? (
            <button
              onClick={() => {
                // No real PIN set yet (still on the "123456" placeholder) — Parent Mode
                // stays open with no prompt until the wizard's PIN step sets one for real.
                if (state.settings.pinConfigured) { setPinError(""); setPinPrompt(true); }
                else setMode("parent");
              }}
              className="relative bg-pill text-navy rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap flex-shrink-0">
              <Icon id={state.settings.pinConfigured ? "ic-lock" : "ic-unlock"} size={16} className="inline-block align-[-3px] mr-1.5" /><span>{ct("parent.parent_mode", "Parent Mode")}</span>
              {/* The pending count is the cheapest possible in-app signal that something is
                  waiting, visible from Kid Mode without a PIN and needing no permission. */}
              {pendingRequestCount > 0 && (
                <span aria-label={`${pendingRequestCount} request${pendingRequestCount === 1 ? "" : "s"} waiting`}
                  className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs font-bold rounded min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {pendingRequestCount}
                </span>
              )}
            </button>
          ) : wizardActive ? null : (
            /* Not until setup is DONE (Alan, 21 Sep). While the wizard is unfinished the
               household is still on the "123456" placeholder, so the Parent Mode button
               opposite this one lets anyone straight back in with no pad — which makes an
               exit to Kid Mode a way to park setup indefinitely with the PIN never set, and
               to hand the device on in that state. The KidModeFab below already could not
               appear during the wizard (it sits in the !wizardActive branch of the ternary);
               this header renders above that ternary, so it was the one way out. Setup cannot
               finish without a PIN, so gating on wizardActive is the same thing as gating on
               "a real PIN exists". */
            <button ref={headerKidModeRef} onClick={() => attemptNav(() => { clearParentUnlocked(code); setMode("child"); })} className="bg-pill text-navy rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap flex-shrink-0"><Icon id="ic-unlock" size={16} className="inline-block align-[-3px] mr-1.5" /><span>{ct("parent.kid_mode", "Kid Mode")}</span></button>
          )}
          {/* R item 6 (Alan, Aug 2026): the household code is not in the header; it lives in
              Parent Mode → Settings → Household with the passcode and Quick join QR. */}
          <InstallButton code={code} className="hidden sm:inline-block" />
        </div>
      </header>

      {/* R-B restructure (Aug 2026): ordinary Parent Mode (wizard finished) no longer has a
          persistent kid-context switcher up here at all (redesign-feature-audit.md's IA
          note: "no persistent kid-context switcher") — ParentProfileSwitcher moved down
          into the Kids tab body (see ParentView.jsx), and the old top pill bar is gone,
          replaced by the bottom tab bar rendered below. Kid Mode's own sticky switcher +
          hero card are unaffected. The wizard-active case (steps 1–3) is DELIBERATELY left
          exactly as it was — same gating condition, same ParentProfileSwitcher-only content
          (the old pill bar's own inner condition already excluded wizardActive, so it never
          showed here regardless) — the wizard's screen composition is out of scope for this
          restructure beyond the one enumerated tidy + the finish-landing tab id. */}
      {/* The wizard shows NO child switcher at all (Alan, 30 Aug 2026). It was already
          hidden on steps 4-6; steps 2 (PIN) and 3 (Token Setup) are household-wide settings
          — a PIN and an exchange rate belong to the household, not to a child — so a
          control for picking a child there is offering a choice that changes nothing. Step 1
          is the children list itself, which has its own affordances. Parent Mode proper is
          unaffected: this switcher is how a parent moves between kids everywhere else. */}
      {(mode === "child" || mode === "parent") && (
      <>
      {/* Status-bar backdrop (8 Sep prod fix). Fixed, so it needs no scroll state: it covers
          the top safe-area band plus the 8px the pinned region rests below, in the canvas
          tint, at the same z-index as the region. At rest it lies over the empty
          SafeAreaSpacer; once the page scrolls, the header and tab content pass under it
          instead of appearing above the pinned region. Phones span the viewport; on wide
          screens it is sized to the column like the tab bars, so the frame stays visible. */}
      <div
        aria-hidden="true"
        data-testid="kid-topbar-backdrop"
        className="fixed top-0 left-0 right-0 z-10 bg-slate-50 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-full md:max-w-[var(--shell-max)]"
        style={{ height: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
      />
      {/* Kid mode (Alan, 10 Sep): the child's name row is NOT pinned — it scrolls away and only
          the hero stays. Rendered before the sticky region so the hero sticks once the name
          has passed the top. */}
      {mode === "child" && <div className="pb-1"><KidProfileSwitcher profiles={visibleProfiles} activeChildId={activeChildId} onSelect={setActiveChildId} onEditAvatar={childTab === "home" ? () => setAvatarOpen((v) => !v) : null} trailing={<InstallButton code={code} variant="icon" className="sm:hidden" />} /></div>}
      {(mode === "parent" || (activeChild && (childTab === "home" || childTab === "rewards"))) && (
      <div
        /* No padding-top, in either mode (Alan, 18 Sep): the pinned card and the parent
           switcher both sit hard against this region's own 8px top offset, with the device's
           safe-area inset above that. The pb logic is untouched. */
        className={`sticky z-10 bg-slate-50 -mx-3 sm:-mx-5 px-3 sm:px-5 ${mode === "child" ? "pb-0" : (parentTab === "kids" ? "pb-5" : "pb-0")}`}
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
      >
        {/* Kid mode (white sheet, decision 45 rev 2 — Alan, 10 Sep): no canvas band under the
            hero; the sheet starts at its bottom edge and, once the page scrolls, slides
            under it. Parent Mode keeps the 20px of air under its switcher. */}
        {mode === "parent" ? (
          /* V.3 (Alan): not shown during the wizard — the strip switches the active child,
             which means nothing mid-setup; the wizard names its own child per step. */
          wizardActive || parentTab !== "kids" ? null : <ParentProfileSwitcher profiles={profiles} activeChildId={activeChildId} onSelect={setActiveChildId} hiddenIds={hiddenIds} />
        ) : (
          <>
            {/* Pass P (Alan, 6 Sep): the hero is pinned here again on every tab — the pass-N
                move into the Home column was rejected on the laptop. On wide screens it is
                a 75%-width band (see HeroCard). The avatar pencil lives on the name line. */}
            {/* 12 Sep staging pass: the compact balance card goes from Earn and Learn — on
                those tabs it is a pinned strip the child scrolls under, not information they
                need. Home keeps the full hero; Rewards keeps the compact one, because there
                the balance is the thing they are about to spend. */}
            {activeChild && (childTab === "home" || childTab === "rewards") && (
              <div>
                <HeroCard child={activeChild} rewardSettings={state.settings} kidTheme={getKidTheme(effectiveAge(activeChild))} compact={childTab !== "home"} avatarOpen={avatarOpen} onToggleAvatar={null} onPickCharacter={childTab === "home" ? () => setAvatarOpen((v) => !v) : null} loan={openLoan(state.requests, activeChild)} />
              </div>
            )}
          </>
        )}
      </div>
      )}
      </>
      )}

      {mode === "parent" ? (
        wizardActive ? (
          <SetupWizard state={state} update={update} updateAndSave={updateAndSave} code={code} showToast={showToast} activeChild={activeChild} setActiveChildId={setActiveChildId} addTransaction={addTransaction} setPendingVerifiedPin={setPendingVerifiedPin} setVerifiedPin={setVerifiedPin} hiddenIds={hiddenIds} onToggleVisibility={toggleChildVisibility} mutedIds={mutedIds} onToggleMute={toggleChildMute}
            onFinished={() => {
              // Land on Home (the default parent tab already, but explicit in case a
              // future change makes wizard completion reachable from elsewhere) and pin
              // scroll to the top so the overlay opens flush against the top of the screen
              // rather than wherever the wizard's last step happened to be scrolled to.
              setParentTab("home");
              window.scrollTo({ top: 0, behavior: "auto" });
              if (!hasSeenSetupComplete()) setShowSetupComplete(true);
              // A new family's first arrival: the waiting-requests overlay (2 Oct) shows once the
              // set-up complete screen is closed, with the homework they signed up with in it.
              overlayArmedRef.current = true;
            }}
          />
        ) : (
          <>
            {requestsOverlayOpen && !overlayBlocked && pendingRequests.length > 0 && (
              <RequestsOverlay
                requests={state.requests}
                profiles={state.profiles}
                rate={state.settings?.exchangeRate > 0 ? state.settings.exchangeRate : 10}
                onOpen={openFromRequestsOverlay}
                onSeeAll={seeAllFromRequestsOverlay}
                onLater={closeRequestsOverlay}
              />
            )}
            {(() => {
              const req = sharedOverlay ? (state.requests || []).find((r) => r.id === sharedOverlay && r.status === "pending" && r.requestType === HW_SHARED_TYPE) : null;
              if (!req) return null;
              return (
                <div className="fixed inset-0 z-50 bg-slate-900/45 overflow-y-auto" role="dialog" aria-label={req.title} onClick={() => setSharedOverlay(null)} data-testid="hw-shared-overlay">
                  <div className="max-w-lg mx-auto bg-slate-50 min-h-full p-3 flex flex-col gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }} onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-extrabold text-slate-900">{ct("hwreceive.overlay_title", "Homework was shared with you")}</div>
                      <button onClick={() => setSharedOverlay(null)} aria-label={ct("hwreceive.close", "Close")} className="w-9 h-9 rounded-lg bg-white text-slate-900 text-lg leading-none" data-testid="hw-shared-overlay-close">×</button>
                    </div>
                    <HomeworkReceive
                      state={state}
                      shared={{ code: req.sharedCode, name: req.title, type: req.listType, lang: req.lang, direction: req.direction, schoolYear: req.schoolYear, pairs: req.pairs }}
                      onAdd={(childId, meta, until, archiveFirst) => { approveHomeworkSheet(req, meta.pairs, until, archiveFirst, { childId, name: meta.name, lang: meta.lang, direction: meta.direction, oinksOn: meta.oinksOn, finishTokens: meta.finishTokens }); setSharedOverlay(null); }}
                      onDiscard={() => { rejectHomeworkSheet(req, ""); setSharedOverlay(null); }}
                      onClose={() => setSharedOverlay(null)}
                    />
                    <p className="text-xs text-slate-400 px-1">{ct("hwreceive.overlay_note", "Close this and it waits for you in Requests.")}</p>
                  </div>
                </div>
              );
            })()}
            <ParentView state={state} update={update} updateAndSave={updateAndSave} activeChild={activeChild} setActiveChildId={setActiveChildId} addTransaction={addTransaction} moveTokensToVault={moveTokensToVault} reverseTransaction={reverseTransaction} reverseRequest={reverseRequest} rejectVaultTransfer={rejectVaultTransfer} showToast={showToast} parentTab={parentTab} setParentTab={setParentTab} code={code} verifiedPin={verifiedPin} setPendingVerifiedPin={setPendingVerifiedPin} setPendingVerifiedPasscode={setPendingVerifiedPasscode} setPendingVerifiedJoinToken={setPendingVerifiedJoinToken} onLeaveHousehold={onLeaveHousehold} hiddenIds={hiddenIds} onToggleVisibility={toggleChildVisibility} mutedIds={mutedIds} onToggleMute={toggleChildMute} unsavedGuardRef={unsavedGuardRef} onOpenSettings={() => attemptNav(() => setParentTab("settings"))} onApproveHomework={approveHomeworkSheet} onRejectHomework={rejectHomeworkSheet}  requestsFocus={requestsFocus} onRequestsFocusHandled={() => setRequestsFocus(null)} onHomeBusyChange={(busy) => { homeBusyRef.current = busy; }} />
            {/* This branch is only reached when !wizardActive (see the ternary above) — the
                same gate the old top pill bar used (mode==="parent" && !wizardActive &&
                parentTab!=="settings") reduces to just the settings check here. */}
            {parentTab !== "settings" && (
              <>
                {/* Spacer so the last card in Parent Mode never sits behind the fixed bottom
                    bar below — same h-24 pattern ChildView.jsx uses above its own fixed nav,
                    replacing the old KidModeFab's fixed inline paddingBottom hack. */}
                <div className="h-24" />
                {!headerKidModeVisible && !softKeyboardOpen && !photoStepOpen && <KidModeFab onClick={() => attemptNav(() => { clearParentUnlocked(code); setMode("child"); })} />}
                {/* LB-376 (Alan, 27 Sep, editing a photographed list on his phone): the bar is
                    fixed to the bottom of the LAYOUT viewport, so the phone's keyboard pushes it
                    up the page and it lands in the middle of the words being edited. It stands
                    down while a field has focus, exactly as the FAB above already does — the
                    same hook, so there is one answer to this and not two. Nothing is lost by
                    hiding it: the bar navigates away from the form, which is not what a parent
                    mid-edit is reaching for, and it returns the moment the field is left. */}
                {!softKeyboardOpen && (
                  <ParentBottomNav
                    parentTab={parentTab}
                    onSelectTab={(id) => { if (id !== parentTab) attemptNav(() => setParentTab(id)); }}
                    pendingCount={pendingRequestCount}
                  />
                )}
              </>
            )}
          </>
        )
      ) : !activeChild ? (
        <WelcomeSetupCard />
      ) : (
        <ChildView
          adults={state.adults || []} /* LB-343 (G1): the names a child sees on their own history */
          code={code}
          onOpenHelp={openHelp}
          child={activeChild}
          muted={mutedIds.includes(activeChild.id)}
          tab={childTab}
          setTab={setChildTab}
          avatarOpen={avatarOpen}
          setAvatarOpen={setAvatarOpen}
          earnFocusTasks={earnFocusTasks}
          setEarnFocusTasks={setEarnFocusTasks}
          earnFocusLearn={earnFocusLearn}
          setEarnFocusLearn={setEarnFocusLearn}
          transactions={state.transactions.filter((t) => t.childId === activeChild.id)}
          requests={state.requests.filter((r) => r.childId === activeChild.id)}
          activities={state.activities || []}
          experienceRewards={state.experienceRewards || []}
          learning={state.learningContent[activeChild.id] || { spellingWords: [], vocabBank: [] }}
          habits={state.habits || []}
          habitLog={(state.habitLog || []).filter((l) => l.childId === activeChild.id)}
          onToggleHabit={toggleHabit}
          rewardSettings={state.settings}
          showToast={showToast}
          onSubmitRequest={(title, amount, image) => submitRequest(activeChild.id, "earn", title, amount, null, image ? { image } : undefined)}
          onRedeemGoal={(goal, image) => submitRequest(activeChild.id, "goal_redeem", `Redeem goal: ${goal.title}`, goalTargetTokens(goal, state.settings?.exchangeRate), goal.id, image ? { image } : undefined)}
          onRequestTransfer={(tokens, pence) => submitVaultTransfer(activeChild.id, tokens, pence)}
          onClaimReward={(reward) => submitRequest(activeChild.id, "experience_redeem", `Redeem reward: ${reward.label}`, reward.tokens, null, { experienceRewardId: reward.id })}
          // LB-17: the child asks a grown-up to buy something. The title IS the item (the
          // parent card and the feed say "wants to buy"); pricePence keeps the price exactly
          // as typed and is what a rate change re-derives requestedAmount from (LB-355).
          onAskToBuy={({ what, pricePence, tokens, image }) => submitRequest(activeChild.id, "ask_to_buy", what, tokens, null, { pricePence, ...(image ? { image } : {}) })}
          onCancelTransfer={cancelRequest}
          // Fires on every vocab question, independent of reward/spending-cap logic below —
          // word mastery is learning progress, not money, so it should always be recorded
          // even on a practice-only (cooldown) attempt or a session that doesn't pass. Once a
          // word has been answered correctly twice in total it drops out of the eligible pool
          // VocabTest draws from (see ChildView.jsx's LearnEarnTab) until a parent either
          // resets it or it's naturally reconsidered — see LearningAdminTab.jsx's "Mastered"
          // badge.
          onVocabWordResult={(wordId, correct) => {
            // A wrong answer on a NOT-yet-mastered word changes nothing (exactly as before) —
            // skip the update() entirely so it doesn't bump the revision and trigger a save.
            // A wrong answer on a MASTERED word is a failed refresher (R3b) and does write.
            if (!correct) {
              const lcNow = state.learningContent[activeChild.id];
              const wNow = lcNow && (lcNow.vocabBank || []).find((v) => v.id === wordId);
              if (!wNow || (wNow.correctCount || 0) < 2) return;
            }
            update((s) => {
              const lc = s.learningContent[activeChild.id];
              const w = lc && (lc.vocabBank || []).find((v) => v.id === wordId);
              if (!w) return s;
              const today = todayStr();
              const wasMastered = (w.correctCount || 0) >= 2;
              if (correct) {
                w.correctCount = (w.correctCount || 0) + 1;
                if (wasMastered) {
                  // Refresher answered right — mastery holds; next refresher in 45 days
                  // (see vocabRefresherDue in lib/utils.js).
                  w.lastRefreshedAt = today;
                } else if (w.correctCount >= 2 && !w.masteredAt) {
                  w.masteredAt = today; // just mastered — first refresher due in 14 days
                }
              } else {
                // Failed refresher — demote to one-correct so the word rejoins normal
                // rotation until re-mastered, which restarts the refresher clock fresh.
                w.correctCount = 1;
                delete w.masteredAt;
                delete w.lastRefreshedAt;
              }
              return s;
            });
          }}
          onLearnEarn={(amount, reason, kind, tier) => {
            if (amount <= 0) return { credited: false };
            // Under the spending cap, Learn & Earn pays out instantly exactly as before. Once
            // this reward would breach or the child is already at/over the cap, it's routed to
            // the parent as a pending request instead of an instant credit — see the
            // 2026-08-15 spending-cap scoping decision. The calling Test component uses the
            // returned { credited, capMessage } to decide whether to show the usual coin
            // celebration or the "sent to your parent" message in its place.
            const child = state.profiles.find((p) => p.id === activeChild.id);
            // LB-248: double token day doubles Learn & Earn too — the per-game weekly cap and
            // the spending cap both still apply to the doubled amount.
            const doubleDay = isDoubleTokenDay(activeChild.id, todayStr(), state.settings);
            const baseAmount = amount;
            if (doubleDay) amount = amount * 2;
            const capStatus = capStatusForNewEarning(child, state.transactions, state.requests, state.settings?.exchangeRate, amount);
            if (capStatus.blocked || capStatus.breaches) {
              // Made outside the updater so the test history can point at this request (LB-366).
              const requestId = uid();
              update((s) => {
                s.requests.unshift({
                  id: requestId,
                  childId: activeChild.id,
                  requestType: "learn_earn_reward",
                  title: `${LEARN_EARN_KIND_LABEL[kind] || "Learn & Earn"} reward — over weekly cap`,
                  requestedAmount: amount,
                  goalId: null,
                  status: "pending",
                  timestamp: nowIso(),
                  breachesCap: true,
                  learnEarnKind: kind,
                  learnEarnTier: tier || null,
                  reason,
                  ...(doubleDay ? { baseAmount, doubleDay: true } : {}),
                });
                return s;
              });
              showToast(ct("toast.sent_for_approval", "Sent to parent for approval"), "info");
              stampHistoryReward(kind, amount, true, requestId);
              return { credited: false, capMessage: "Nice work. You've reached your earning limit for the week, so your parent gets to decide on this one." };
            }
            addTransaction(activeChild.id, amount, reason, "credit", "token", undefined, { earnCap: true, learnEarnKind: kind, learnEarnTier: tier || null, ...(doubleDay ? { baseAmount, doubleDay: true } : {}) });
            stampHistoryReward(kind, amount, false);
            // Every Learn & Earn game (English: spelling/vocab, Maths: ttstreaks/mathsworkout)
            // tracks its own weekly payout usage under `${kind}PayoutWeek` / `${kind}PayoutCount`
            // — the weekly cap itself is looked up per-category in LearnEarnTab (English vs
            // Maths), so this just needs to bump the right kind's counter generically.
            const LEARN_EARN_KINDS = ["spelling", "vocab", "ttstreaks", "mathsworkout"];
            if (LEARN_EARN_KINDS.includes(kind)) {
              update((s) => {
                if (!s.learningContent[activeChild.id]) s.learningContent[activeChild.id] = { spellingWords: [], vocabBank: [] };
                const lc = s.learningContent[activeChild.id];
                const wk = weekStartOf(todayStr());
                const weekField = `${kind}PayoutWeek`;
                const countField = `${kind}PayoutCount`;
                const tiersField = `${kind}PayoutTiers`;
                const fresh = lc[weekField] !== wk;
                lc[countField] = fresh ? 1 : (lc[countField] || 0) + 1;
                lc[tiersField] = [...(fresh ? [] : lc[tiersField] || []), tier].filter(Boolean);
                lc[weekField] = wk;
                // Untick every word that was included in the test that just paid out, so a full-marks
                // pass doesn't let a kid cycle through the same words again next week without a
                // parent re-selecting them.
                // Not when Learning Coach ran the test: under Coach the custom list is not in play
                // (ChildView's spellingTestWords), so a Coach test must leave the ticks alone.
                const spellingList = (lc.spellingAuto || {}).list;
                const coachRanSpelling = coachEnabled(state.settings) && (!spellingList || spellingList === "auto") && resolveSpellingList(activeChild, spellingList) !== "off";
                if (kind === "spelling" && !coachRanSpelling) {
                  (lc.spellingWords || []).forEach((w) => { if (w.testInclude !== false) w.testInclude = false; });
                }
                return s;
              });
            }
            return { credited: true };
          }}
          // ---- Oinks (fun-only currency — see lib/oinks.js) ----
          // Deliberately NOT routed through addTransaction: the money ledger stays money-only
          // (Law 1). All three updaters run ensureCosmetics first because profiles created
          // before Oinks existed — and preview-mode profiles, which never pass through
          // migrateState — have neither the balance nor the cosmetics fields.
          onEarnOinks={(op) => {
            if (!op || op <= 0) return;
            update((s) => {
              const lc = s.learningContent[activeChild.id];
              if (lc && lc.oinksEnabled === false) return s; // parent switched Oinks off mid-session
              const c = s.profiles.find((p) => p.id === activeChild.id);
              if (!c) return s;
              ensureCosmetics(c);
              c.oinks += op;
              recordOinkEarn(c, op); // LB-168: feeds "at your pace"
              return s;
            });
          }}
          onBuyCosmetic={(itemId) => {
            const item = findOinkItem(itemId);
            if (!item) return;
            const current = state.profiles.find((p) => p.id === activeChild.id);
            const balance = current && typeof current.oinks === "number" ? current.oinks : 0;
            if (balance < item.price) { showToast(ct("toast.not_enough_oinks", "Not enough Oinks yet. Keep practising"), "info"); return; }
            update((s) => {
              const c = s.profiles.find((p) => p.id === activeChild.id);
              if (!c) return s;
              ensureCosmetics(c);
              if (c.oinks < item.price || c.cosmetics.owned.includes(item.id)) return s; // re-check inside the updater
              c.oinks -= item.price;
              c.cosmetics.owned.push(item.id);
              // Auto-equip on purchase (scope decision #6) — via applyEquip so the
              // outfit/torso exclusion (Aug 2026 wardrobe expansion: pyjamas are a
              // one-piece) holds on the purchase path too, not just the Wear it button.
              applyEquip(c, item.slot, item.id);
              return s;
            });
            showToast(ct("toast.item_purchased", "{label} is yours", { label: item.label }), "good");
            track("oink_purchase", { kind: "cosmetic", band: ageBand(activeChild) || undefined, value: item.price });
          }}
          onEquipCosmetic={(slot, itemId) => {
            update((s) => {
              const c = s.profiles.find((p) => p.id === activeChild.id);
              if (!c) return s;
              ensureCosmetics(c);
              // Only ever equip something actually owned; null always allowed (take it off).
              if (itemId !== null && !c.cosmetics.owned.includes(itemId)) return s;
              // applyEquip (lib/oinks.js) rather than a direct assignment: it also clears
              // the excluded counterpart slot when wearing pyjamas (outfit) or a torso item.
              applyEquip(c, slot, itemId);
              return s;
            });
          }}
          // ---- Characters (Aug 2026 characters/economy task) — same shape as the cosmetic
          // handlers just above, deliberately: a parallel catalog/ownership/equip path rather
          // than folding characters into OINK_CATALOG's slot system, per the task brief.
          siblings={visibleProfiles.filter((p) => p.id !== activeChild.id)}
          onGiftOinks={(toId, amount) => {
            // LB-247: child → sibling transfer; guardrails live in lib/oinks.js.
            let result = { ok: false };
            update((s) => { result = giftOinks(s, activeChild.id, toId, amount, todayStr(), nowIso()); return result.ok ? s : null; });
            if (result.ok) {
              const to = state.profiles.find((p) => p.id === toId);
              showToast(ct("toast.oink_gift_sent", "{n} Oinks sent to {name}", { n: result.amount, name: to ? to.name : "" }), "good");
              track("oink_gift_sibling", { value: result.amount });
            } else {
              showToast(result.reason === "today" ? ct("toast.oink_gift_today", "You already sent Oinks to them today") : result.reason === "balance" ? ct("toast.oink_gift_balance", "Not enough Oinks for that") : ct("toast.oink_gift_min", "Gifts start at 5 Oinks"), "bad");
            }
          }}
          onClearLevelUp={() => update((s) => { const lc = s.learningContent[activeChild.id]; if (!lc || !lc.adaptive || !lc.adaptive.pendingLevelUp) return null; delete lc.adaptive.pendingLevelUp; return s; }, { auto: true })}
          // LB-364: one finished homework go. Writes the go into the list (progress, goes,
          // Oinks earned) and a test-history row under "homework", and returns whether the
          // list became complete on this go. HomeworkRound then pays the finishing tokens,
          // if any, through onLearnEarn above — the same cap path every test uses — so the
          // history stamp lands on the row this call recorded. Nothing reaches the engine.
          // Premium (2 Oct): with homework locked the child's Add new homework is simply not shown
          // (no prop, so the kid component draws its plain Homework heading); their live homework
          // still shows and plays.
          onSubmitHomework={homeworkUnlocked(state.settings) ? (pairs, dropped, listType) => submitHomeworkSheet(activeChild.id, pairs, dropped, listType) : undefined}
          onHomeworkGo={(code, results, historyRows, goOpts = {}) => {
            const childId = activeChild.id;
            const before = findHomeworkList(state.learningContent[childId], code);
            if (!before) return null;
            const lc0 = state.learningContent[childId];
            const oinksPaid = before.oinksOn && lc0.oinksEnabled !== false ? results.filter((r) => r.correct).length : 0;
            // The outcome is worked out HERE, on a copy of the list as this render has it, and
            // never read back out of the updater. update() hands its function to React's
            // setState, which runs it later unless the component has no update pending, so a
            // value assigned inside it is usually still null when this returns (Alan, prod, 29
            // Sep: a child reached 6 of 6 known and the finishing tokens never paid, because the
            // Oinks paid during the go had already queued an update on Root). The updater then
            // applies the same go to the stored list, with the same day and time.
            const today = todayStr(); const at = nowIso();
            const goFlags = { stopped: !!goOpts.stopped };
            const outcome = applyGo(JSON.parse(JSON.stringify(before)), results, today, at, goFlags);
            // A go left part way is a stopped row, which never pays, UNLESS it made every word
            // known: then the finishing tokens pay (HomeworkRound), so it is recorded as a go that
            // pays and the tokens are stamped on it. Hence recorded after the outcome is known.
            recordHistory(childId, "homework", { total: results.length, selection: "manual", history: { questions: historyRows, oinks: oinksPaid, qualified: null, gateOpen: true, passMark: null, passKind: "pct" } }, !!goOpts.stopped && !outcome.completedNow);
            update((s) => {
              const lc = s.learningContent[childId];
              const list = findHomeworkList(lc, code);
              if (!list) return null;
              applyGo(list, results, today, at, goFlags);
              pruneHomework(lc, today);
              return s;
            });
            return outcome;
          }}
          onClearOinkGift={() => update((s) => { const c = s.profiles.find((p) => p.id === activeChild.id); if (!c || !c.pendingOinkGift) return null; delete c.pendingOinkGift; return s; }, { auto: true })}
          onClearRateNote={() => update((s) => { const c = s.profiles.find((p) => p.id === activeChild.id); if (!c || !c.pendingRateNote) return null; delete c.pendingRateNote; return s; }, { auto: true })}
          onBuyCharacter={(charId) => {
            const item = findCharacterItem(charId);
            if (!item) return;
            const current = state.profiles.find((p) => p.id === activeChild.id);
            const balance = current && typeof current.oinks === "number" ? current.oinks : 0;
            if (balance < item.price) { showToast(ct("toast.not_enough_oinks", "Not enough Oinks yet. Keep practising"), "info"); return; }
            update((s) => {
              const c = s.profiles.find((p) => p.id === activeChild.id);
              if (!c) return s;
              ensureCosmetics(c);
              if (c.oinks < item.price || c.characters.owned.includes(item.id)) return s; // re-check inside the updater
              c.oinks -= item.price;
              c.characters.owned.push(item.id);
              // Auto-equip on purchase, same as cosmetics (scope decision #6) — a character
              // just bought is the one a child wants to see right away.
              c.characters.equipped = item.id;
              return s;
            });
            showToast(ct("toast.character_purchased", "{label} is yours", { label: characterName(item.id) }), "good");
            track("oink_purchase", { kind: "character", band: ageBand(activeChild) || undefined, value: item.price });
          }}
          onEquipCharacter={(charId) => {
            update((s) => {
              const c = s.profiles.find((p) => p.id === activeChild.id);
              if (!c) return s;
              ensureCosmetics(c);
              // null (Piggington) is always allowed — he's never in `owned`, he's the default.
              // LB-168: starters are owned by definition and never stored in `owned` — go
              // through ownedCharacters(), which includes them (the "can't wear the Bear
              // back" bug Alan hit on staging, 7 Sep).
              if (charId !== null && !ownedCharacters(c).includes(charId)) return s;
              c.characters.equipped = charId;
              c.identityChosen = true; // wearing a character from the shop is choosing one
              return s;
            });
          }}
          // ---- Trophy Cabinet stats + Daily Question (libs/badges.js, lib/dailyQuestion.js) ----
          onItemResults={(kind, results, abandoned = false, opts = {}) => {
            // Adaptive (LB-19): per-item results feed the engine; band moves notify the parent.
            // Nothing here touches rewards or caps.
            const game = ["spelling", "vocab", "mathsworkout", "ttstreaks"].includes(kind) ? kind : null;
            if (!game) return;
            // LB-366: the test history is written for EVERY settled or stopped test — Coach on
            // or off, whoever chose the words — before either engine path below runs.
            if (results && results.length) recordHistory(activeChild.id, game, opts, abandoned);
            // Two cases write a log entry and nothing else (Alan, 22 Sep):
            //  - a stopped-early test, in any household. Labelled in Coach, paying nothing
            //    and moving nothing — deliberately NOT the engine's own `abandoned` path,
            //    which counts a quit towards moving the level DOWN.
            //  - any finished test in a household with Learning Coach OFF, where this
            //    handler used to return before recording anything, so a manual family's
            //    Learn & Earn history simply did not exist.
            // Both go through the wrapper (recordLogOnly), never the engine.
            if (abandoned || !coachEnabled(state.settings)) {
              if (!results || !results.length) return;
              update((s) => {
                if (!s.learningContent[activeChild.id]) s.learningContent[activeChild.id] = { spellingWords: [], vocabBank: [] };
                const lc = s.learningContent[activeChild.id];
                lc.adaptive = lc.adaptive || {};
                const byAge = game === "spelling" ? resolveSpellingList(activeChild, (lc.spellingAuto || {}).list)
                  : game === "vocab" ? resolveVocabCategory(activeChild, lc.vocabCategory)
                  : game === "mathsworkout" ? resolveMathsBand(activeChild, lc.mathsBand) : null;
                const st = adaptiveGameState(lc.adaptive, game, byAge);
                lc.adaptive[game] = recordLogOnly(st, {
                  day: todayStr(),
                  score: results.filter((r) => r.correct).length,
                  total: opts.total ?? results.length,
                  ...(abandoned ? { answered: results.length } : {}),
                  source: abandoned ? "stopped" : "manual",
                });
                return s;
              }, { auto: true });
              return;
            }
            const params = adaptiveParams(cmsConfig("adaptive.params"));
            let events = [];
            update((s) => {
              const lc = s.learningContent[activeChild.id]; if (!lc) return null;
              lc.adaptive = lc.adaptive || {};
              const byAge = game === "spelling" ? resolveSpellingList(activeChild, (lc.spellingAuto || {}).list)
                : game === "vocab" ? resolveVocabCategory(activeChild, lc.vocabCategory)
                : game === "mathsworkout" ? resolveMathsBand(activeChild, lc.mathsBand) : null;
              const st = adaptiveGameState(lc.adaptive, game, byAge);
              const bandOrder = game === "spelling" ? effectiveSpellingListOrder() : game === "vocab" ? effectiveVocabCategoryOrder() : game === "mathsworkout" ? effectiveMathsBandOrder() : [];
              const topicsInBand = game === "spelling" ? [...new Set(spellingListItems(st.band || byAge).map((w) => w.topic).filter(Boolean))]
                : game === "vocab" ? [1, 2, 3, 4, 5].map((g) => `${st.band || byAge}:g${g}`)
                : game === "mathsworkout" ? mathsBandTypeIds(st.band || byAge)
                : (lc.ttTables && lc.ttTables.length ? lc.ttTables : []).map((t) => `table-${t}`);
              // LB-278: WHO chose the items decides which path runs. A parent-set or school
              // list goes to the wrapper, which never lets the result reach a level or a band
              // — not by a flag inside the engine, but by keeping only the fields a custom
              // test may write. The kid surface is the only place that knows which it was.
              const r = opts.selection === "manual"
                ? recordCustomSession(st, results, { day: todayStr(), match: game === "spelling" ? curriculumItemForWord : null, params })
                : adaptiveRecord(st, results, { day: todayStr(), kind: "session", abandoned, bandOrder, topicsInBand }, params);
              lc.adaptive[game] = r.state; events = r.events;
              // LB-280: per-word counts live in their OWN slice, not on engine state, so this
              // ticket reverts by deleting a module and a field. Only engine-drawn answers
              // count — see recordWordResults for why.
              if (!lc.coachWords || typeof lc.coachWords !== "object") lc.coachWords = {};
              lc.coachWords[game] = recordWordResults(lc.coachWords[game], results, { day: todayStr(), fromEngine: opts.selection !== "manual" });
              // Marks the Learn tab reads (zip 3): when the target last rose (▲ for a fortnight),
              // the latest message (kept a day), and a pending level-up moment shown once.
              const gameName = { spelling: "Spelling", vocab: "Vocabulary", mathsworkout: "Maths", ttstreaks: "Times tables" }[game];
              for (const e of events) {
                if (e.type === "target_up") lc.adaptive[game].targetUpAt = todayStr();
                if (e.type === "topic_mastered") {
                  const label = game === "spelling" ? e.topic.replace(/-/g, " ") : game === "ttstreaks" ? `the ${e.topic.replace("table-", "")} times table` : game === "mathsworkout" ? e.topic.replace(/_/g, " ") : null;
                  if (label) lc.adaptive.lastEvent = { type: "mastered", day: todayStr(), text: ct("kid.learn.msg_mastered", "{topic}: mastered. That one's yours now.", { topic: label.charAt(0).toUpperCase() + label.slice(1) }) };
                }
                if (e.type === "band_up") {
                  const bandLabel = game === "spelling" ? (effectiveSpellingLists()[e.to]?.label || e.to) : game === "vocab" ? (effectiveVocabCategories()[e.to]?.label || e.to) : (effectiveMathsBands()[e.to]?.label || e.to);
                  lc.adaptive.pendingLevelUp = { game, band: bandLabel, day: todayStr() };
                  lc.adaptive.lastEvent = { type: "band_up", day: todayStr(), text: ct("kid.learn.msg_levelup_band", "{game} just moved up to {band}.", { game: gameName, band: bandLabel }) };
                  const label = game === "spelling" ? (effectiveSpellingLists()[e.to]?.label || e.to) : game === "vocab" ? (effectiveVocabCategories()[e.to]?.label || e.to) : (effectiveMathsBands()[e.to]?.label || e.to);
                  const feedGame = { spelling: "spelling", vocab: "vocabulary", mathsworkout: "maths", ttstreaks: "times tables" }[game];
                  const reason = ct("adaptive.feed.band_up", "{name} has moved up to {band} in {game}. Pin them back in Learn & Earn if that's too soon.", { name: activeChild.name, band: label, game: feedGame });
                  s.transactions.unshift({ id: uid(), childId: activeChild.id, amount: 0, reason, type: "credit", balance: "token", kind: "adaptive_band_up", timestamp: nowIso() });
                  // The push goes from the worker on save (worker/lib/notifications.js
                  // notifyForAdaptiveBandUp diffs adaptive[game].movedUpAt) — no client send.
                }
                if (e.type === "band_down") {
                  // Maths Ladder v2: quiet for the kid (no message, no toast — the same rule as
                  // target_down), a feed line and a push for the parent, who can pin.
                  const label = game === "spelling" ? (effectiveSpellingLists()[e.to]?.label || e.to) : game === "vocab" ? (effectiveVocabCategories()[e.to]?.label || e.to) : (effectiveMathsBands()[e.to]?.label || e.to);
                  const feedGame = { spelling: "spelling", vocab: "vocabulary", mathsworkout: "maths", ttstreaks: "times tables" }[game];
                  const reason = ct("adaptive.feed.band_down", "Learning Coach has moved {name} to {band} in {game} for a while. Pin them in Learn & Earn if you'd rather they stayed put.", { name: activeChild.name, band: label, game: feedGame });
                  s.transactions.unshift({ id: uid(), childId: activeChild.id, amount: 0, reason, type: "credit", balance: "token", kind: "adaptive_band_down", timestamp: nowIso() });
                }
              }
              return s;
            }, { auto: true });
            if (events.some((e) => e.type === "target_up") && !events.some((e) => e.type === "band_up")) showToast(ct("adaptive.toast.target_up", "Great work. We're going to challenge you a bit more now."), "good");
            else if (events.some((e) => e.type === "topic_mastered") && !events.some((e) => e.type === "band_up")) { const t = events.find((e) => e.type === "topic_mastered"); const lab = game === "spelling" ? t.topic.replace(/-/g, " ") : game === "ttstreaks" ? `the ${t.topic.replace("table-", "")} times table` : game === "mathsworkout" ? t.topic.replace(/_/g, " ") : null; if (lab) showToast(ct("kid.learn.msg_mastered", "{topic}: mastered. That one's yours now.", { topic: lab.charAt(0).toUpperCase() + lab.slice(1) }), "good"); }
          }}
          onSessionFinished={(kind, correct, answered, perfect) => {
            update((s) => {
              if (!s.learningContent[activeChild.id]) s.learningContent[activeChild.id] = { spellingWords: [], vocabBank: [] };
              const lc = s.learningContent[activeChild.id];
              if (!lc.stats || typeof lc.stats !== "object") lc.stats = {};
              const st = lc.stats;
              st.questionsAnswered = (st.questionsAnswered || 0) + (answered || 0);
              st.sessionsCompleted = (st.sessionsCompleted || 0) + 1;
              if (!st.perfect || typeof st.perfect !== "object") st.perfect = {};
              if (perfect) st.perfect[kind] = (st.perfect[kind] || 0) + 1;
              // All-Rounder tracking: the set of game kinds played in the CURRENT week only —
              // rolls over (resets) whenever a session lands in a new week.
              const week = weekStartOf(todayStr());
              if (!st.weekKinds || st.weekKinds.week !== week) st.weekKinds = { week, kinds: [] };
              if (!st.weekKinds.kinds.includes(kind)) st.weekKinds.kinds.push(kind);
              return s;
            });
            awardNewBadges(activeChild.id);
          }}
          onAnswerDailyQuestion={(result) => {
            const today = todayStr();
            const lcNow = state.learningContent[activeChild.id];
            if (lcNow && lcNow.dailyQ && lcNow.dailyQ.date === today) return; // already answered today (double-submit guard)
            const op = dailyQuestionOP(result, isDoubleOinkDay(activeChild.id, today));
            let justCompletedPerfectWeek = false;
            update((s) => {
              if (!s.learningContent[activeChild.id]) s.learningContent[activeChild.id] = { spellingWords: [], vocabBank: [] };
              const lc = s.learningContent[activeChild.id];
              if (lc.dailyQ && lc.dailyQ.date === today) return s; // re-check inside the updater
              // Perfect Week (Aug 2026 characters/economy task) — recordDailyQuestionForPerfectWeek
              // (lib/dailyQuestion.js) is the pure, unit-tested core; this is just the one write
              // site for its result. Correctness doesn't matter here, only that a question got
              // answered (DAILY_OP_WRONG's philosophy).
              if (!lc.stats || typeof lc.stats !== "object") lc.stats = {};
              const { dailyQWeek, perfectWeek } = recordDailyQuestionForPerfectWeek(lc.stats.dailyQWeek, today);
              lc.stats.dailyQWeek = dailyQWeek;
              justCompletedPerfectWeek = perfectWeek;
              lc.dailyQ = { date: today, result, perfectWeek };
              // The daily question counts toward Century/Big Brain — it's a question answered.
              lc.stats.questionsAnswered = (lc.stats.questionsAnswered || 0) + 1;
              if (lc.oinksEnabled !== false) {
                const c = s.profiles.find((p) => p.id === activeChild.id);
                if (c) {
                  ensureCosmetics(c);
                  c.oinks += op;
                  if (perfectWeek) c.oinks += PERFECT_WEEK_OP;
                  recordOinkEarn(c, op + (perfectWeek ? PERFECT_WEEK_OP : 0));
                }
              }
              return s;
            });
            if (justCompletedPerfectWeek) setPerfectWeekCelebrateKey((k) => k + 1);
            awardNewBadges(activeChild.id);
            track("daily_question_answered", { band: ageBand(activeChild) || undefined });
          }}
          perfectWeekCelebrateKey={perfectWeekCelebrateKey}
          badgeCelebrateKey={badgeCelebrateKey}
          onChangeAvatar={(patch) => updateChildAvatar(activeChild.id, patch)}
          onUpdateGoals={(newGoals) => {
            // W2.5: a grown list means a kid just created a goal (edits/removals keep or
            // shrink the length). Parent-side creation goes through GoalsSection's own
            // update path and isn't counted in v1 — the funnel cares about kid initiative.
            if (Array.isArray(newGoals) && newGoals.length > (activeChild.goals || []).length) {
              track("goal_created", { band: ageBand(activeChild) || undefined });
            }
            update((s) => { const c = s.profiles.find((p) => p.id === activeChild.id); if (c) c.goals = newGoals; return s; });
          }}
        />
      )}

      {/* In Kid Mode, ChildView renders its own copy of these inside the scrollable content
          (above its fixed bottom nav) — rendering them again here would just sit hidden
          behind that nav, so skip the shell's copy for that case. */}
      {!(mode === "child" && activeChild) && (
        <>
          {/* R-D (Aug 2026, punch-list 9): when the parent bottom bar is showing, pad the
              footer block clear of it — it was sitting hidden behind the fixed bar. */}
          <AppLegalFooter
            onHelp={() => openHelp()}
            className={`mt-6 ${mode === "parent" && !wizardActive && parentTab !== "settings" ? "pb-24" : "pb-2"}`}
          />
          <CopyrightFooter />
        </>
      )}
    </div>
    </PullToRefresh>
  );
}

/* ------------------------------ child view -------------------------------- */

// The balance display, reused everywhere a kid sees their totals. `compact` swaps the tall
// two-column gradient card (Home) for a single-line strip (Earn/Rewards) — same numbers, same
// gradient identity, far less vertical space. Avatar editing only applies to the full card.

// R-B parent-mode restructure (Aug 2026): bottom tab bar, 3 tabs — replaces the old top
// pill bar's 4 (adjust/requests/profiles/learning). `requests`/`profiles` ids are retained
// (the `/?goto=requests` deep link and Wizard.jsx's ChildChipRow mounts depend on ids, not
// labels); `adjust`/`learning` are retired — Rewards moves into Home + the Kids-tab detail,
// Learn & Earn moves inside the Kids-tab detail (see ParentView.jsx's KidDetail). R-C polish
// (Aug 2026, punch-list 10): icons come from the tintable sprite (docs/ui-icons.md — these
// three ids were drawn FOR this bar), tinted via currentColor so active/inactive states just
// follow the button's text colour. The emoji were an R-B improvisation, not the design.
export const PARENT_TABS = [
  // F.1 (Alan, Sep 2026): each tab's icon in its family colour when inactive — Home navy,
  // Kids teal (the vault/family hue), Requests indigo. Active = the pill, navy icon.
  { id: "home", label: "Home", icon: "ic-home", tone: "#1B2A4A" },
  { id: "profiles", label: "Kids", icon: "ic-kids", tone: "#0F9B8E" },
  { id: "requests", label: "Requests", icon: "ic-requests", tone: "#5B5BD6" },
];
// Kid Mode's primary accent scales with age: under-9s get the brighter "candy vault"
// look (teal + a playful pink progress fill), 9+ gets the calmer "modern mint" look
// (deeper teal throughout). Parent Mode is untouched by this — it stays neutral slate.

export const PTR_THRESHOLD = 70;

export const PTR_MAX_PULL = 100;
